import React, { useState, useMemo, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { format, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths, isWithinInterval, eachWeekOfInterval, eachDayOfInterval, eachMonthOfInterval, addDays } from 'date-fns';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import {
  DollarSign, TrendingUp, TrendingDown, Users, Clock, Calendar,
  Briefcase, Download, Filter, ChevronDown, CheckCircle, AlertCircle,
  BarChart3, PieChart, FileText, Percent, ArrowUp, ArrowDown,
  ClipboardList, MapPin, Camera, Navigation, Play, Flag, ChevronRight, X, Eye,
  Timer, AlertTriangle, Car, Edit2, Save, Phone, MessageSquare, Mail, Grid, Activity
} from 'lucide-react';
import { formatCurrency } from '../utils/helpers';
import { openWeeklyPayPdf } from '../utils/weeklyPayPdf';
import './Reports.css';

// Convert decimal hours to h:mm format for payroll compliance
const formatHoursMinutes = (decimalHours) => {
  if (!decimalHours || decimalHours === 0) return '0:00';
  const h = Math.floor(decimalHours);
  const m = Math.round((decimalHours - h) * 60);
  // Handle edge case where rounding gives 60 minutes
  if (m === 60) return `${h + 1}:00`;
  return `${h}:${m.toString().padStart(2, '0')}`;
};

function Reports() {
  const { state, updateJob, getJobInvoice } = useApp();
  
  // Date range state
  const [dateRange, setDateRange] = useState('this_month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [activeReport, setActiveReport] = useState('revenue');
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [photoViewer, setPhotoViewer] = useState(null); // { url, type, timestamp }
  const [taExpandedEmp, setTaExpandedEmp] = useState(null); // time analysis expanded employee
  const [taTimeRecords, setTaTimeRecords] = useState([]);
  const [taView, setTaView] = useState('week'); // day | week | month
  const [trendPeriod, setTrendPeriod] = useState('weekly'); // weekly | monthly

  // Payroll grid dedicated date range state
  const [payrollStartDate, setPayrollStartDate] = useState('');
  const [payrollEndDate, setPayrollEndDate] = useState('');
  const [payrollTimeRecords, setPayrollTimeRecords] = useState([]);

  // Time override edit state
  const [editingTimeJobId, setEditingTimeJobId] = useState(null);
  const [timeEditStart, setTimeEditStart] = useState('');
  const [timeEditEnd, setTimeEditEnd] = useState('');
  const [timeEditNote, setTimeEditNote] = useState('');
  const [savingTimeEdit, setSavingTimeEdit] = useState(false);

  // Open time edit for a job
  const openTimeEdit = (job) => {
    setEditingTimeJobId(job.id);
    // Pre-fill with existing times (HH:MM format for time inputs)
    const parseToTimeStr = (val) => {
      if (!val) return '';
      try {
        const d = typeof val === 'string' ? parseISO(val) : val.toDate ? val.toDate() : val;
        return format(d, 'HH:mm');
      } catch { return ''; }
    };
    setTimeEditStart(parseToTimeStr(job.actual_start) || parseToTimeStr(job.enRouteAt) || job.scheduledTime || '09:00');
    setTimeEditEnd(parseToTimeStr(job.completedAt || job.actual_end) || '');
    setTimeEditNote('');
  };

  // Save time override
  const saveTimeOverride = async (job) => {
    if (!timeEditNote.trim()) {
      alert('Please provide a reason for the time override.');
      return;
    }
    if (!timeEditStart || !timeEditEnd) {
      alert('Please enter both start and end times.');
      return;
    }

    setSavingTimeEdit(true);
    try {
      // Build new ISO timestamps using the job's date
      const jobDate = job.date; // yyyy-MM-dd
      const newStart = new Date(`${jobDate}T${timeEditStart}:00`);
      const newEnd = new Date(`${jobDate}T${timeEditEnd}:00`);
      
      if (newEnd <= newStart) {
        alert('End time must be after start time.');
        setSavingTimeEdit(false);
        return;
      }

      const newDurationMin = Math.round((newEnd - newStart) / 60000);
      const newDurationHours = Math.round((newDurationMin / 60) * 100) / 100;

      // Recalculate labor cost
      const allWorkers = [...new Set([...(job.team || []), ...(job.helpers || [])])];
      let newLaborCost = 0;
      allWorkers.forEach(empId => {
        const emp = state.employees.find(e => e.id === empId);
        const rate = emp?.hourlyRate || emp?.payRate || 15;
        newLaborCost += newDurationHours * rate;
      });
      newLaborCost = Math.round(newLaborCost * 100) / 100;
      const revenue = job.price || 0;
      const newLaborPercent = revenue > 0 ? Math.round((newLaborCost / revenue) * 100) : 0;

      await updateJob(job.id, {
        // Save originals (only on first override, don't overwrite previous originals)
        ...(!job.timeOverride ? {
          original_actual_start: job.actual_start || null,
          original_actual_end: job.actual_end || job.completedAt || null,
          original_actual_duration_min: job.actual_duration_min || null,
        } : {}),
        // Set new times
        actual_start: newStart.toISOString(),
        actual_end: newEnd.toISOString(),
        completedAt: newEnd.toISOString(),
        actual_duration_min: newDurationMin,
        actual_duration_hours: newDurationHours,
        laborCost: newLaborCost,
        profitMargin: Math.round((revenue - newLaborCost) * 100) / 100,
        laborPercent: newLaborPercent,
        // Flag as overridden
        timeOverride: true,
        timeOverrideNote: timeEditNote.trim(),
        timeOverrideAt: new Date().toISOString(),
        timeOverrideBy: 'admin',
      });

      setEditingTimeJobId(null);
      setTimeEditNote('');
    } catch (err) {
      console.error('Error saving time override:', err);
      alert('Failed to save time override.');
    }
    setSavingTimeEdit(false);
  };

  // Calculate date range
  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    switch (dateRange) {
      case 'this_week':
        return { startDate: startOfWeek(now), endDate: endOfWeek(now) };
      case 'last_week':
        return { startDate: startOfWeek(subWeeks(now, 1)), endDate: endOfWeek(subWeeks(now, 1)) };
      case 'this_month':
        return { startDate: startOfMonth(now), endDate: endOfMonth(now) };
      case 'last_month':
        return { startDate: startOfMonth(subMonths(now, 1)), endDate: endOfMonth(subMonths(now, 1)) };
      case 'last_3_months':
        return { startDate: startOfMonth(subMonths(now, 2)), endDate: endOfMonth(now) };
      case 'custom':
        return { 
          startDate: customStart ? parseISO(customStart) : startOfMonth(now),
          endDate: customEnd ? parseISO(customEnd) : endOfMonth(now)
        };
      default:
        return { startDate: startOfMonth(now), endDate: endOfMonth(now) };
    }
  }, [dateRange, customStart, customEnd]);

  // Load time records for the date range (for Time Analysis)
  useEffect(() => {
    const loadTaRecords = async () => {
      try {
        const s = format(startDate, 'yyyy-MM-dd');
        const e = format(endDate, 'yyyy-MM-dd');
        const q = query(collection(db, 'timeRecords'), where('date', '>=', s), where('date', '<=', e));
        const snap = await getDocs(q);
        setTaTimeRecords(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) { console.error('Error loading time records:', err); }
    };
    loadTaRecords();
  }, [startDate, endDate]);

  // Filter jobs by date range
  const filteredJobs = useMemo(() => {
    return state.jobs.filter(job => {
      if (!job.date) return false;
      const jobDate = parseISO(job.date);
      return isWithinInterval(jobDate, { start: startDate, end: endDate });
    });
  }, [state.jobs, startDate, endDate]);

  // Filter invoices by date range
  const filteredInvoices = useMemo(() => {
    return state.invoices.filter(inv => {
      if (!inv.createdAt) return false;
      const invDate = parseISO(inv.createdAt);
      return isWithinInterval(invDate, { start: startDate, end: endDate });
    });
  }, [state.invoices, startDate, endDate]);

  // ===== PAYROLL GRID DATE RANGE =====
  // Payroll grid uses its own date range; defaults to the global date range
  const payrollEffectiveStart = useMemo(() => {
    return payrollStartDate ? parseISO(payrollStartDate) : startDate;
  }, [payrollStartDate, startDate]);

  const payrollEffectiveEnd = useMemo(() => {
    return payrollEndDate ? parseISO(payrollEndDate) : endDate;
  }, [payrollEndDate, endDate]);

  // Load time records for payroll grid date range
  useEffect(() => {
    const loadPayrollTimeRecords = async () => {
      try {
        const s = format(payrollEffectiveStart, 'yyyy-MM-dd');
        const e = format(payrollEffectiveEnd, 'yyyy-MM-dd');
        const q = query(collection(db, 'timeRecords'), where('date', '>=', s), where('date', '<=', e));
        const snap = await getDocs(q);
        setPayrollTimeRecords(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) { console.error('Error loading payroll time records:', err); }
    };
    loadPayrollTimeRecords();
  }, [payrollEffectiveStart, payrollEffectiveEnd]);

  // Filter jobs for payroll grid (uses payroll-specific date range)
  const payrollFilteredJobs = useMemo(() => {
    return state.jobs.filter(job => {
      if (!job.date) return false;
      const jobDate = parseISO(job.date);
      return isWithinInterval(jobDate, { start: payrollEffectiveStart, end: payrollEffectiveEnd });
    });
  }, [state.jobs, payrollEffectiveStart, payrollEffectiveEnd]);

  // ===== REVENUE REPORT =====
  const revenueData = useMemo(() => {
    const completedJobs = filteredJobs.filter(j => j.status === 'completed');
    const paidInvoices = filteredInvoices.filter(i => i.status === 'paid');
    
    // Total revenue from paid invoices
    const totalRevenue = paidInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
    
    // Revenue by service type
    const byServiceType = {};
    completedJobs.forEach(job => {
      const type = job.serviceType || 'other';
      if (!byServiceType[type]) {
        byServiceType[type] = { count: 0, revenue: 0 };
      }
      byServiceType[type].count++;
      byServiceType[type].revenue += job.price || 0;
    });

    // Revenue by week
    const weeks = eachWeekOfInterval({ start: startDate, end: endDate });
    const byWeek = weeks.map(weekStart => {
      const weekEnd = endOfWeek(weekStart);
      const weekJobs = completedJobs.filter(job => {
        const jobDate = parseISO(job.date);
        return isWithinInterval(jobDate, { start: weekStart, end: weekEnd });
      });
      const weekInvoices = paidInvoices.filter(inv => {
        const invDate = parseISO(inv.paidAt || inv.createdAt);
        return isWithinInterval(invDate, { start: weekStart, end: weekEnd });
      });
      return {
        week: format(weekStart, 'MMM d'),
        jobs: weekJobs.length,
        revenue: weekInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0)
      };
    });

    // Outstanding invoices
    const outstanding = filteredInvoices
      .filter(i => i.status === 'pending' || i.status === 'overdue')
      .reduce((sum, inv) => sum + (inv.total || 0), 0);

    // Tips collected
    const totalTips = paidInvoices.reduce((sum, inv) => sum + (inv.tipAmount || 0), 0);

    return {
      totalRevenue,
      jobCount: completedJobs.length,
      avgJobValue: completedJobs.length > 0 ? totalRevenue / completedJobs.length : 0,
      byServiceType,
      byWeek,
      outstanding,
      totalTips
    };
  }, [filteredJobs, filteredInvoices, startDate, endDate]);

  // ===== LABOR COST REPORT =====
  const laborData = useMemo(() => {
    const completedJobs = filteredJobs.filter(j => j.status === 'completed');
    
    // Calculate labor cost per job
    const jobsWithLabor = completedJobs.map(job => {
      const actualHours = job.actualDuration ? job.actualDuration / 60 : job.duration || 0;
      let laborCost = 0;
      
      // Calculate based on team members + helpers
      const allWorkers = [...(job.team || []), ...(job.helpers || [])];
      if (allWorkers.length > 0) {
        allWorkers.forEach(empId => {
          const emp = state.employees.find(e => e.id === empId);
          const hourlyRate = emp?.hourlyRate || 15;
          laborCost += actualHours * hourlyRate;
        });
      } else {
        // Default: assume 2 people at $15/hr
        laborCost = actualHours * 15 * 2;
      }

      const revenue = job.price || 0;
      const laborPercent = revenue > 0 ? (laborCost / revenue) * 100 : 0;
      const profit = revenue - laborCost;

      return {
        ...job,
        actualHours,
        laborCost,
        laborPercent,
        profit
      };
    });

    // Totals
    const totalRevenue = jobsWithLabor.reduce((sum, j) => sum + (j.price || 0), 0);
    const totalLaborCost = jobsWithLabor.reduce((sum, j) => sum + j.laborCost, 0);
    const overallLaborPercent = totalRevenue > 0 ? (totalLaborCost / totalRevenue) * 100 : 0;
    const totalProfit = totalRevenue - totalLaborCost;

    // By employee (include helpers)
    const byEmployee = {};
    jobsWithLabor.forEach(job => {
      const allWorkers = [...new Set([...(job.team || []), ...(job.helpers || [])])];
      if (allWorkers.length > 0) {
        allWorkers.forEach(empId => {
          const emp = state.employees.find(e => e.id === empId);
          if (!emp) return;
          const empName = `${emp.firstName} ${emp.lastName || ''}`.trim();
          if (!byEmployee[empId]) {
            byEmployee[empId] = {
              name: empName,
              jobs: 0,
              hours: 0,
              laborCost: 0,
              revenue: 0
            };
          }
          byEmployee[empId].jobs++;
          byEmployee[empId].hours += (job.actualHours || 0) / allWorkers.length;
          byEmployee[empId].laborCost += job.laborCost / allWorkers.length;
          byEmployee[empId].revenue += (job.price || 0) / allWorkers.length;
        });
      }
    });

    // By service type
    const byServiceType = {};
    jobsWithLabor.forEach(job => {
      const type = job.serviceType || 'other';
      if (!byServiceType[type]) {
        byServiceType[type] = { jobs: 0, revenue: 0, laborCost: 0 };
      }
      byServiceType[type].jobs++;
      byServiceType[type].revenue += job.price || 0;
      byServiceType[type].laborCost += job.laborCost;
    });

    // Jobs over 40% labor
    const overBudgetJobs = jobsWithLabor.filter(j => j.laborPercent > 40);

    return {
      totalRevenue,
      totalLaborCost,
      overallLaborPercent,
      totalProfit,
      jobsWithLabor,
      byEmployee: Object.values(byEmployee),
      byServiceType,
      overBudgetJobs
    };
  }, [filteredJobs, state.employees]);

  // ===== JOB REPORT =====
  const jobReportData = useMemo(() => {
    const allJobs = filteredJobs;
    const completed = allJobs.filter(j => j.status === 'completed');
    const cancelled = allJobs.filter(j => j.status === 'cancelled');
    const scheduled = allJobs.filter(j => j.status === 'scheduled');

    // On-time vs late (comparing actual start to scheduled)
    let onTime = 0;
    let late = 0;
    completed.forEach(job => {
      if (job.actualStart && job.scheduledTime) {
        const scheduled = job.scheduledTime.split(':').map(Number);
        const actual = job.actualStart.split('T')[1]?.split(':').map(Number) || [0, 0];
        const scheduledMins = scheduled[0] * 60 + scheduled[1];
        const actualMins = actual[0] * 60 + actual[1];
        if (actualMins <= scheduledMins + 15) { // 15 min grace
          onTime++;
        } else {
          late++;
        }
      } else {
        onTime++; // Assume on time if no data
      }
    });

    // Time variance (expected vs actual)
    let totalExpectedMins = 0;
    let totalActualMins = 0;
    completed.forEach(job => {
      totalExpectedMins += (job.duration || 0) * 60;
      totalActualMins += job.actualDuration || (job.duration || 0) * 60;
    });
    const avgTimeVariance = completed.length > 0 
      ? ((totalActualMins - totalExpectedMins) / completed.length)
      : 0;

    // By service type
    const byServiceType = {};
    allJobs.forEach(job => {
      const type = job.serviceType || 'other';
      if (!byServiceType[type]) {
        byServiceType[type] = { total: 0, completed: 0, cancelled: 0 };
      }
      byServiceType[type].total++;
      if (job.status === 'completed') byServiceType[type].completed++;
      if (job.status === 'cancelled') byServiceType[type].cancelled++;
    });

    // By day of week
    const byDayOfWeek = [0, 0, 0, 0, 0, 0, 0]; // Sun-Sat
    allJobs.forEach(job => {
      if (job.date) {
        const day = parseISO(job.date).getDay();
        byDayOfWeek[day]++;
      }
    });

    return {
      total: allJobs.length,
      completed: completed.length,
      cancelled: cancelled.length,
      scheduled: scheduled.length,
      completionRate: allJobs.length > 0 ? (completed.length / (completed.length + cancelled.length)) * 100 : 0,
      onTime,
      late,
      onTimeRate: (onTime + late) > 0 ? (onTime / (onTime + late)) * 100 : 100,
      avgTimeVariance,
      byServiceType,
      byDayOfWeek
    };
  }, [filteredJobs]);

  // ===== PAYROLL REPORT (Actual Hours from Time Records) =====
  const payrollData = useMemo(() => {
    const paidInvoices = filteredInvoices.filter(i => i.status === 'paid');

    // Calculate hours and pay per employee from actual time records
    const employeeData = {};

    // Use time records for actual clocked hours
    payrollTimeRecords.forEach(rec => {
      if (!rec.employeeId || !rec.hoursWorked) return;
      const emp = state.employees.find(e => e.id === rec.employeeId);
      if (!emp) return;

      const empId = rec.employeeId;
      const effectiveRate = rec.clockInRate || emp.hourlyRate || 15;
      if (!employeeData[empId]) {
        employeeData[empId] = {
          id: empId,
          name: `${emp.firstName} ${emp.lastName || ''}`.trim(),
          hourlyRate: effectiveRate,
          regularHours: 0,
          jobs: 0,
          tips: 0,
          weightedRateSum: 0,
          weightedHoursSum: 0
        };
      }
      employeeData[empId].regularHours += rec.hoursWorked;
      employeeData[empId].weightedRateSum = (employeeData[empId].weightedRateSum || 0) + (effectiveRate * rec.hoursWorked);
      employeeData[empId].weightedHoursSum = (employeeData[empId].weightedHoursSum || 0) + rec.hoursWorked;
    });

    // Count jobs per employee (for reference)
    const completedJobs = payrollFilteredJobs.filter(j => j.status === 'completed');
    completedJobs.forEach(job => {
      const allWorkers = [...new Set([...(job.team || []), ...(job.helpers || [])])];
      allWorkers.forEach(empId => {
        if (employeeData[empId]) {
          employeeData[empId].jobs++;
        }
      });
    });

    // Fallback: if no time records, use job-based hours
    if (payrollTimeRecords.length === 0) {
      completedJobs.forEach(job => {
        const allWorkers = [...new Set([...(job.team || []), ...(job.helpers || [])])];
        if (allWorkers.length === 0) return;
        const hoursPerPerson = (job.actual_duration_min ? job.actual_duration_min / 60 : job.actualDuration ? job.actualDuration / 60 : job.duration || 0) / allWorkers.length;

        allWorkers.forEach(empId => {
          const emp = state.employees.find(e => e.id === empId);
          if (!emp) return;

          if (!employeeData[empId]) {
            employeeData[empId] = {
              id: empId,
              name: `${emp.firstName} ${emp.lastName || ''}`.trim(),
              hourlyRate: emp.hourlyRate || 15,
              regularHours: 0,
              jobs: 0,
              tips: 0
            };
          }

          employeeData[empId].regularHours += hoursPerPerson;
          employeeData[empId].jobs++;
        });
      });
    }

    // Add tips from invoices
    paidInvoices.forEach(inv => {
      if (inv.tipSplit && inv.tipSplit.length > 0) {
        inv.tipSplit.forEach(split => {
          if (employeeData[split.employeeId]) {
            employeeData[split.employeeId].tips += split.amount || 0;
          }
        });
      } else if (inv.tipAmount > 0) {
        // Fallback: split tip among the job's team when tipSplit is missing
        const job = inv.jobId ? payrollFilteredJobs.find(j => j.id === inv.jobId) || state.jobs.find(j => j.id === inv.jobId) : null;
        if (job) {
          const allWorkers = [...new Set([...(job.team || []), ...(job.helpers || [])])].filter(id => employeeData[id]);
          if (allWorkers.length > 0) {
            const splitAmount = inv.tipAmount / allWorkers.length;
            allWorkers.forEach(empId => {
              employeeData[empId].tips += Math.round(splitAmount * 100) / 100;
            });
          }
        }
      }
    });

    // Calculate gross pay (use weighted average rate if multiple rates were used)
    const employees = Object.values(employeeData).map(emp => {
      const effectiveRate = emp.weightedHoursSum > 0
        ? emp.weightedRateSum / emp.weightedHoursSum
        : emp.hourlyRate;
      return {
        ...emp,
        hourlyRate: Math.round(effectiveRate * 100) / 100,
        regularPay: emp.weightedRateSum > 0 ? emp.weightedRateSum : emp.regularHours * emp.hourlyRate,
        grossPay: (emp.weightedRateSum > 0 ? emp.weightedRateSum : emp.regularHours * emp.hourlyRate) + emp.tips
      };
    });

    // Totals
    const totalHours = employees.reduce((sum, e) => sum + e.regularHours, 0);
    const totalRegularPay = employees.reduce((sum, e) => sum + e.regularPay, 0);
    const totalTips = employees.reduce((sum, e) => sum + e.tips, 0);
    const totalGrossPay = employees.reduce((sum, e) => sum + e.grossPay, 0);

    return {
      employees: employees.sort((a, b) => b.regularHours - a.regularHours),
      totalHours,
      totalRegularPay,
      totalTips,
      totalGrossPay
    };
  }, [payrollTimeRecords, payrollFilteredJobs, filteredInvoices, state.employees, state.jobs]);

  // ===== PAYROLL SPREADSHEET DATA (Actual Hours from Time Records) =====
  const payrollSpreadsheet = useMemo(() => {
    // Build list of all days in the payroll date range
    const days = eachDayOfInterval({ start: payrollEffectiveStart, end: payrollEffectiveEnd });

    // Build per-employee, per-day hours from actual time records (clock-in/clock-out)
    const empDayHours = {};

    payrollTimeRecords.forEach(rec => {
      if (!rec.employeeId || !rec.date || !rec.hoursWorked) return;
      const emp = state.employees.find(e => e.id === rec.employeeId);
      if (!emp) return;

      const empId = rec.employeeId;
      const recDate = rec.date; // yyyy-MM-dd

      if (!empDayHours[empId]) {
        empDayHours[empId] = {
          id: empId,
          name: `${emp.firstName} ${emp.lastName || ''}`.trim(),
          hourlyRate: emp.hourlyRate || 15,
          days: {}
        };
      }

      if (!empDayHours[empId].days[recDate]) {
        empDayHours[empId].days[recDate] = 0;
      }
      empDayHours[empId].days[recDate] += rec.hoursWorked;
    });

    // If no time records exist, fall back to job-based hours so the grid isn't empty
    if (payrollTimeRecords.length === 0) {
      const completedJobs = payrollFilteredJobs.filter(j => j.status === 'completed');
      completedJobs.forEach(job => {
        const allWorkers = [...new Set([...(job.team || []), ...(job.helpers || [])])];
        if (allWorkers.length === 0) return;
        const hoursPerPerson = (job.actual_duration_min ? job.actual_duration_min / 60 : job.actualDuration ? job.actualDuration / 60 : job.duration || 0) / allWorkers.length;
        const jobDate = job.date;

        allWorkers.forEach(empId => {
          const emp = state.employees.find(e => e.id === empId);
          if (!emp) return;

          if (!empDayHours[empId]) {
            empDayHours[empId] = {
              id: empId,
              name: `${emp.firstName} ${emp.lastName || ''}`.trim(),
              hourlyRate: emp.hourlyRate || 15,
              days: {}
            };
          }

          if (!empDayHours[empId].days[jobDate]) {
            empDayHours[empId].days[jobDate] = 0;
          }
          empDayHours[empId].days[jobDate] += hoursPerPerson;
        });
      });
    }

    // Convert to sorted array
    const employees = Object.values(empDayHours).sort((a, b) => a.name.localeCompare(b.name));

    // Calculate row totals (total hours and pay per employee)
    employees.forEach(emp => {
      emp.totalHours = Object.values(emp.days).reduce((sum, h) => sum + h, 0);
      emp.totalPay = emp.totalHours * emp.hourlyRate;
    });

    // Calculate column totals (total hours per day)
    const dayTotals = {};
    days.forEach(day => {
      const dayStr = format(day, 'yyyy-MM-dd');
      dayTotals[dayStr] = employees.reduce((sum, emp) => sum + (emp.days[dayStr] || 0), 0);
    });

    // Grand totals
    const grandTotalHours = employees.reduce((sum, emp) => sum + emp.totalHours, 0);
    const grandTotalPay = employees.reduce((sum, emp) => sum + emp.totalPay, 0);

    return { employees, days, dayTotals, grandTotalHours, grandTotalPay };
  }, [payrollTimeRecords, payrollFilteredJobs, state.employees, payrollEffectiveStart, payrollEffectiveEnd]);

  // ===== WEEKLY PAY REPORT (Per-Week Breakdown by Pay Rate) =====
  const weeklyPayReport = useMemo(() => {
    // Split the payroll date range into individual weeks (Mon-Sun)
    const weeks = eachWeekOfInterval(
      { start: payrollEffectiveStart, end: payrollEffectiveEnd },
      { weekStartsOn: 1 }
    );

    const weekData = weeks.map(weekStart => {
      const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
      // Clamp to the payroll effective range
      const clampedStart = weekStart < payrollEffectiveStart ? payrollEffectiveStart : weekStart;
      const clampedEnd = weekEnd > payrollEffectiveEnd ? payrollEffectiveEnd : weekEnd;
      const startStr = format(clampedStart, 'yyyy-MM-dd');
      const endStr = format(clampedEnd, 'yyyy-MM-dd');
      const label = `${format(clampedStart, 'MMM d')} – ${format(clampedEnd, 'MMM d')}`;

      // Filter time records for this week
      const weekRecords = payrollTimeRecords.filter(r => r.date >= startStr && r.date <= endStr);

      // Build per-employee, per-rate breakdown
      const empMap = {};

      weekRecords.forEach(rec => {
        if (!rec.employeeId || !rec.hoursWorked) return;
        const emp = state.employees.find(e => e.id === rec.employeeId);
        if (!emp) return;
        const empId = rec.employeeId;
        const rate = rec.clockInRate || emp.hourlyRate || 15;
        const rateLabel = rec.clockInRole || 'Standard';

        if (!empMap[empId]) {
          empMap[empId] = {
            id: empId,
            name: `${emp.firstName} ${emp.lastName || ''}`.trim(),
            rates: {},
            totalHours: 0,
            grossPay: 0
          };
        }

        const rateKey = `${rate}_${rateLabel}`;
        if (!empMap[empId].rates[rateKey]) {
          empMap[empId].rates[rateKey] = { rate, label: rateLabel, hours: 0, pay: 0 };
        }
        empMap[empId].rates[rateKey].hours += rec.hoursWorked;
        empMap[empId].rates[rateKey].pay += rec.hoursWorked * rate;
        empMap[empId].totalHours += rec.hoursWorked;
        empMap[empId].grossPay += rec.hoursWorked * rate;
      });

      // Fallback: if no time records, use job-based hours
      if (weekRecords.length === 0) {
        const weekJobs = payrollFilteredJobs.filter(j =>
          j.status === 'completed' && j.date >= startStr && j.date <= endStr
        );
        weekJobs.forEach(job => {
          const allWorkers = [...new Set([...(job.team || []), ...(job.helpers || [])])];
          if (allWorkers.length === 0) return;
          const hoursPerPerson = (job.actual_duration_min ? job.actual_duration_min / 60 : job.actualDuration ? job.actualDuration / 60 : job.duration || 0) / allWorkers.length;

          allWorkers.forEach(empId => {
            const emp = state.employees.find(e => e.id === empId);
            if (!emp) return;
            const rate = emp.hourlyRate || 15;
            const rateLabel = 'Standard';
            const rateKey = `${rate}_${rateLabel}`;

            if (!empMap[empId]) {
              empMap[empId] = {
                id: empId,
                name: `${emp.firstName} ${emp.lastName || ''}`.trim(),
                rates: {},
                totalHours: 0,
                grossPay: 0
              };
            }
            if (!empMap[empId].rates[rateKey]) {
              empMap[empId].rates[rateKey] = { rate, label: rateLabel, hours: 0, pay: 0 };
            }
            empMap[empId].rates[rateKey].hours += hoursPerPerson;
            empMap[empId].rates[rateKey].pay += hoursPerPerson * rate;
            empMap[empId].totalHours += hoursPerPerson;
            empMap[empId].grossPay += hoursPerPerson * rate;
          });
        });
      }

      const employees = Object.values(empMap).sort((a, b) => a.name.localeCompare(b.name));
      // Convert rates object to sorted array for each employee
      employees.forEach(emp => {
        emp.rateBreakdown = Object.values(emp.rates).sort((a, b) => b.hours - a.hours);
      });

      const weekTotalHours = employees.reduce((sum, e) => sum + e.totalHours, 0);
      const weekGrossPay = employees.reduce((sum, e) => sum + e.grossPay, 0);

      return {
        label,
        startStr,
        endStr,
        employees,
        totalHours: weekTotalHours,
        grossPay: weekGrossPay
      };
    });

    // Grand totals across all weeks
    const grandTotalHours = weekData.reduce((sum, w) => sum + w.totalHours, 0);
    const grandGrossPay = weekData.reduce((sum, w) => sum + w.grossPay, 0);

    return { weeks: weekData, grandTotalHours, grandGrossPay };
  }, [payrollTimeRecords, payrollFilteredJobs, state.employees, payrollEffectiveStart, payrollEffectiveEnd]);

  // Export Weekly Pay Report to CSV
  const exportWeeklyPayCSV = () => {
    const { weeks } = weeklyPayReport;
    const rows = [['Employee', 'Week', 'Pay Rate Label', 'Hourly Rate', 'Hours', 'Pay', 'Week Total Hours', 'Week Gross Pay']];

    weeks.forEach(week => {
      week.employees.forEach(emp => {
        emp.rateBreakdown.forEach((rb, idx) => {
          rows.push([
            `"${emp.name}"`,
            `"${week.label}"`,
            `"${rb.label}"`,
            `$${rb.rate.toFixed(2)}`,
            formatHoursMinutes(rb.hours),
            `$${rb.pay.toFixed(2)}`,
            idx === 0 ? formatHoursMinutes(emp.totalHours) : '',
            idx === 0 ? `$${emp.grossPay.toFixed(2)}` : ''
          ]);
        });
      });
      // Week totals row
      rows.push([
        `"WEEK TOTAL"`,
        `"${week.label}"`,
        '', '', '',
        '',
        formatHoursMinutes(week.totalHours),
        `$${week.grossPay.toFixed(2)}`
      ]);
      rows.push([]); // Blank separator
    });

    // Grand totals
    rows.push([
      '"GRAND TOTAL"', '', '', '', '', '',
      formatHoursMinutes(weeklyPayReport.grandTotalHours),
      `$${weeklyPayReport.grandGrossPay.toFixed(2)}`
    ]);

    const csv = rows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `weekly-pay-report-${format(payrollEffectiveStart, 'yyyy-MM-dd')}-to-${format(payrollEffectiveEnd, 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Export payroll spreadsheet to CSV
  const exportPayrollSpreadsheetCSV = () => {
    const { employees, days, dayTotals, grandTotalHours, grandTotalPay } = payrollSpreadsheet;
    const dayHeaders = days.map(d => format(d, 'EEE M/d'));
    const headers = ['Employee', 'Rate', ...dayHeaders, 'Total Hrs', 'Total Pay'];

    const rows = employees.map(emp => [
      emp.name,
      `$${emp.hourlyRate.toFixed(2)}`,
      ...days.map(d => {
        const h = emp.days[format(d, 'yyyy-MM-dd')] || 0;
        return h > 0 ? formatHoursMinutes(h) : '';
      }),
      formatHoursMinutes(emp.totalHours),
      `$${emp.totalPay.toFixed(2)}`
    ]);

    // Totals row
    rows.push([
      'TOTAL',
      '',
      ...days.map(d => {
        const t = dayTotals[format(d, 'yyyy-MM-dd')] || 0;
        return t > 0 ? formatHoursMinutes(t) : '';
      }),
      formatHoursMinutes(grandTotalHours),
      `$${grandTotalPay.toFixed(2)}`
    ]);

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll-spreadsheet-${format(startDate, 'yyyy-MM-dd')}-to-${format(endDate, 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Export payroll to CSV
  const exportPayrollCSV = () => {
    const headers = ['Employee Name', 'Hours Worked', 'Hourly Rate', 'Regular Pay', 'Tips', 'Gross Pay'];
    const rows = payrollData.employees.map(emp => [
      emp.name,
      formatHoursMinutes(emp.regularHours),
      emp.hourlyRate.toFixed(2),
      emp.regularPay.toFixed(2),
      emp.tips.toFixed(2),
      emp.grossPay.toFixed(2)
    ]);

    // Add totals row
    rows.push([
      'TOTAL',
      formatHoursMinutes(payrollData.totalHours),
      '',
      payrollData.totalRegularPay.toFixed(2),
      payrollData.totalTips.toFixed(2),
      payrollData.totalGrossPay.toFixed(2)
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll-${format(startDate, 'yyyy-MM-dd')}-to-${format(endDate, 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ===== TREND ANALYSIS DATA =====
  const trendData = useMemo(() => {
    const now = new Date();
    const allJobs = state.jobs;
    const allInvoices = state.invoices;

    // Build periods (last 12 weeks or last 12 months)
    let periods;
    if (trendPeriod === 'weekly') {
      const weeksBack = 12;
      const rangeStart = startOfWeek(subWeeks(now, weeksBack - 1));
      const rangeEnd = endOfWeek(now);
      const weekStarts = eachWeekOfInterval({ start: rangeStart, end: rangeEnd });
      periods = weekStarts.map(ws => ({
        start: ws,
        end: endOfWeek(ws),
        label: format(ws, 'MMM d'),
        shortLabel: format(ws, 'M/d')
      }));
    } else {
      const monthsBack = 12;
      const rangeStart = startOfMonth(subMonths(now, monthsBack - 1));
      const rangeEnd = endOfMonth(now);
      const monthStarts = eachMonthOfInterval({ start: rangeStart, end: rangeEnd });
      periods = monthStarts.map(ms => ({
        start: ms,
        end: endOfMonth(ms),
        label: format(ms, 'MMM yyyy'),
        shortLabel: format(ms, 'MMM')
      }));
    }

    const periodData = periods.map(period => {
      const periodJobs = allJobs.filter(job => {
        if (!job.date) return false;
        try {
          const jobDate = parseISO(job.date);
          return isWithinInterval(jobDate, { start: period.start, end: period.end });
        } catch { return false; }
      });

      const completedJobs = periodJobs.filter(j => j.status === 'completed');

      // Revenue from completed jobs
      const revenue = completedJobs.reduce((sum, j) => sum + (j.price || 0), 0);

      // Still need paid invoices for tips
      const periodInvoices = allInvoices.filter(inv => {
        if (!inv.createdAt) return false;
        try {
          const invDate = parseISO(inv.paidAt || inv.createdAt);
          return isWithinInterval(invDate, { start: period.start, end: period.end });
        } catch { return false; }
      });
      const paidInvoices = periodInvoices.filter(i => i.status === 'paid');

      // Jobs
      const totalJobs = periodJobs.length;
      const completedCount = completedJobs.length;
      const cancelledCount = periodJobs.filter(j => j.status === 'cancelled').length;

      // Avg job value
      const avgJobValue = completedCount > 0
        ? completedJobs.reduce((sum, j) => sum + (j.price || 0), 0) / completedCount
        : 0;

      // Payroll (labor cost)
      let payrollCost = 0;
      let totalHours = 0;
      completedJobs.forEach(job => {
        const allWorkers = [...new Set([...(job.team || []), ...(job.helpers || [])])];
        const actualHours = job.actual_duration_min ? job.actual_duration_min / 60 : job.actualDuration ? job.actualDuration / 60 : job.duration || 0;
        if (allWorkers.length > 0) {
          allWorkers.forEach(empId => {
            const emp = state.employees.find(e => e.id === empId);
            const rate = emp?.hourlyRate || emp?.payRate || 15;
            payrollCost += actualHours * rate;
          });
          totalHours += actualHours * allWorkers.length;
        } else {
          payrollCost += actualHours * 15 * 2;
          totalHours += actualHours * 2;
        }
      });

      // Labor %
      const laborPercent = revenue > 0 ? (payrollCost / revenue) * 100 : 0;

      // Profit
      const profit = revenue - payrollCost;

      // Avg actual duration (in minutes)
      const avgDuration = completedCount > 0
        ? completedJobs.reduce((sum, j) => sum + (j.actual_duration_min || (j.duration ? j.duration * 60 : 0)), 0) / completedCount
        : 0;

      // Tips
      const tips = paidInvoices.reduce((sum, inv) => sum + (inv.tipAmount || 0), 0);

      return {
        label: period.label,
        shortLabel: period.shortLabel,
        revenue,
        totalJobs,
        completedJobs: completedCount,
        cancelledJobs: cancelledCount,
        avgJobValue,
        payrollCost,
        totalHours,
        laborPercent,
        profit,
        avgDuration,
        tips
      };
    });

    // Calculate change from previous period for latest
    const calcChange = (key) => {
      if (periodData.length < 2) return { value: 0, percent: 0 };
      const current = periodData[periodData.length - 1][key];
      const previous = periodData[periodData.length - 2][key];
      const diff = current - previous;
      const pct = previous > 0 ? (diff / previous) * 100 : 0;
      return { value: diff, percent: pct };
    };

    const latest = periodData[periodData.length - 1] || {};

    return {
      periods: periodData,
      current: latest,
      changes: {
        revenue: calcChange('revenue'),
        totalJobs: calcChange('totalJobs'),
        payrollCost: calcChange('payrollCost'),
        profit: calcChange('profit'),
        laborPercent: calcChange('laborPercent'),
        avgJobValue: calcChange('avgJobValue'),
        avgDuration: calcChange('avgDuration'),
        tips: calcChange('tips')
      }
    };
  }, [state.jobs, state.invoices, state.employees, trendPeriod]);

  // Service type labels
  const serviceTypeLabels = {
    recurring: 'Recurring',
    deep_clean: 'Deep Clean',
    one_time: 'One-Time',
    move: 'Move In/Out',
    commercial: 'Commercial',
    other: 'Other'
  };

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Show loading spinner until Firestore data is ready
  if (state.loading && !state.initialized) {
    return (
      <div className="reports-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" />
          <p>Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>Reports</h1>
          <p className="header-subtitle">Business analytics and insights</p>
        </div>
      </div>

      {/* Controls */}
      <div className="reports-controls">
        <div className="report-tabs">
          <button 
            className={`report-tab ${activeReport === 'revenue' ? 'active' : ''}`}
            onClick={() => setActiveReport('revenue')}
          >
            <DollarSign size={18} />
            Revenue
          </button>
          <button 
            className={`report-tab ${activeReport === 'labor' ? 'active' : ''}`}
            onClick={() => setActiveReport('labor')}
          >
            <Percent size={18} />
            Labor Cost
          </button>
          <button 
            className={`report-tab ${activeReport === 'jobs' ? 'active' : ''}`}
            onClick={() => setActiveReport('jobs')}
          >
            <Briefcase size={18} />
            Job Report
          </button>
          <button 
            className={`report-tab ${activeReport === 'payroll' ? 'active' : ''}`}
            onClick={() => setActiveReport('payroll')}
          >
            <Users size={18} />
            Payroll
          </button>
          <button
            className={`report-tab ${activeReport === 'payroll_spreadsheet' ? 'active' : ''}`}
            onClick={() => setActiveReport('payroll_spreadsheet')}
          >
            <Grid size={18} />
            Payroll Grid
          </button>
          <button
            className={`report-tab ${activeReport === 'weekly_pay' ? 'active' : ''}`}
            onClick={() => setActiveReport('weekly_pay')}
          >
            <FileText size={18} />
            Weekly Pay
          </button>
          <button
            className={`report-tab ${activeReport === 'joblog' ? 'active' : ''}`}
            onClick={() => setActiveReport('joblog')}
          >
            <ClipboardList size={18} />
            Job Log
          </button>
          <button
            className={`report-tab ${activeReport === 'timeanalysis' ? 'active' : ''}`}
            onClick={() => setActiveReport('timeanalysis')}
          >
            <Timer size={18} />
            Time Analysis
          </button>
          <button
            className={`report-tab ${activeReport === 'communications' ? 'active' : ''}`}
            onClick={() => setActiveReport('communications')}
          >
            <Phone size={18} />
            Communications
          </button>
          <button
            className={`report-tab ${activeReport === 'trends' ? 'active' : ''}`}
            onClick={() => setActiveReport('trends')}
          >
            <Activity size={18} />
            Trends
          </button>
          <button
            className={`report-tab ${activeReport === 'tips' ? 'active' : ''}`}
            onClick={() => setActiveReport('tips')}
          >
            <DollarSign size={18} />
            Tips
          </button>
        </div>

        <div className="date-filter">
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="this_week">This Week</option>
            <option value="last_week">Last Week</option>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="last_3_months">Last 3 Months</option>
            <option value="custom">Custom Range</option>
          </select>
          
          {dateRange === 'custom' && (
            <div className="custom-dates">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
              />
              <span>to</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Date Range Display */}
      <div className="date-range-display">
        <Calendar size={16} />
        <span>{format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}</span>
      </div>

      {/* ===== REVENUE REPORT ===== */}
      {activeReport === 'revenue' && (
        <div className="report-content">
          {/* KPI Cards */}
          <div className="kpi-grid">
            <div className="kpi-card primary">
              <div className="kpi-icon">
                <DollarSign size={24} />
              </div>
              <div className="kpi-data">
                <span className="kpi-value">{formatCurrency(revenueData.totalRevenue)}</span>
                <span className="kpi-label">Total Revenue</span>
              </div>
            </div>
            <div className="kpi-card">
              <div className="kpi-data">
                <span className="kpi-value">{revenueData.jobCount}</span>
                <span className="kpi-label">Jobs Completed</span>
              </div>
            </div>
            <div className="kpi-card">
              <div className="kpi-data">
                <span className="kpi-value">{formatCurrency(revenueData.avgJobValue)}</span>
                <span className="kpi-label">Avg Job Value</span>
              </div>
            </div>
            <div className="kpi-card warning">
              <div className="kpi-data">
                <span className="kpi-value">{formatCurrency(revenueData.outstanding)}</span>
                <span className="kpi-label">Outstanding</span>
              </div>
            </div>
          </div>

          {/* Revenue by Week */}
          <div className="report-section">
            <h3>Revenue by Week</h3>
            <div className="chart-container">
              <div className="bar-chart">
                {revenueData.byWeek.map((week, idx) => {
                  const maxRevenue = Math.max(...revenueData.byWeek.map(w => w.revenue), 1);
                  const height = (week.revenue / maxRevenue) * 100;
                  return (
                    <div key={idx} className="bar-item">
                      <div className="bar-value">{formatCurrency(week.revenue)}</div>
                      <div className="bar" style={{ height: `${height}%` }} />
                      <div className="bar-label">{week.week}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Revenue by Service Type */}
          <div className="report-section">
            <h3>Revenue by Service Type</h3>
            <div className="data-table">
              <div className="table-header">
                <span>Service Type</span>
                <span>Jobs</span>
                <span>Revenue</span>
                <span>Avg/Job</span>
              </div>
              {Object.entries(revenueData.byServiceType).map(([type, data]) => (
                <div key={type} className="table-row">
                  <span className="service-type">{serviceTypeLabels[type] || type}</span>
                  <span>{data.count}</span>
                  <span className="revenue">{formatCurrency(data.revenue)}</span>
                  <span>{formatCurrency(data.count > 0 ? data.revenue / data.count : 0)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===== LABOR COST REPORT ===== */}
      {activeReport === 'labor' && (
        <div className="report-content">
          {/* KPI Cards */}
          <div className="kpi-grid">
            <div className={`kpi-card ${laborData.overallLaborPercent > 40 ? 'danger' : laborData.overallLaborPercent > 35 ? 'warning' : 'success'}`}>
              <div className="kpi-icon">
                <Percent size={24} />
              </div>
              <div className="kpi-data">
                <span className="kpi-value">{laborData.overallLaborPercent.toFixed(1)}%</span>
                <span className="kpi-label">Labor Cost %</span>
                <span className="kpi-target">Target: 40%</span>
              </div>
            </div>
            <div className="kpi-card">
              <div className="kpi-data">
                <span className="kpi-value">{formatCurrency(laborData.totalLaborCost)}</span>
                <span className="kpi-label">Total Labor Cost</span>
              </div>
            </div>
            <div className="kpi-card">
              <div className="kpi-data">
                <span className="kpi-value">{formatCurrency(laborData.totalRevenue)}</span>
                <span className="kpi-label">Total Revenue</span>
              </div>
            </div>
            <div className="kpi-card success">
              <div className="kpi-data">
                <span className="kpi-value">{formatCurrency(laborData.totalProfit)}</span>
                <span className="kpi-label">Gross Profit</span>
              </div>
            </div>
          </div>

          {/* Labor by Employee */}
          <div className="report-section">
            <h3>Labor Cost by Employee</h3>
            <div className="data-table">
              <div className="table-header">
                <span>Employee</span>
                <span>Jobs</span>
                <span>Hours</span>
                <span>Labor Cost</span>
                <span>Labor %</span>
              </div>
              {laborData.byEmployee.map(emp => {
                const laborPct = emp.revenue > 0 ? (emp.laborCost / emp.revenue) * 100 : 0;
                return (
                  <div key={emp.name} className="table-row">
                    <span className="employee-name">{emp.name}</span>
                    <span>{emp.jobs}</span>
                    <span>{emp.hours.toFixed(1)} hrs</span>
                    <span>{formatCurrency(emp.laborCost)}</span>
                    <span className={`labor-pct ${laborPct > 40 ? 'high' : ''}`}>
                      {laborPct.toFixed(1)}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Jobs Over Budget */}
          {laborData.overBudgetJobs.length > 0 && (
            <div className="report-section warning-section">
              <h3><AlertCircle size={18} /> Jobs Over 40% Labor</h3>
              <div className="data-table">
                <div className="table-header">
                  <span>Date</span>
                  <span>Client</span>
                  <span>Revenue</span>
                  <span>Labor</span>
                  <span>Labor %</span>
                </div>
                {laborData.overBudgetJobs.slice(0, 10).map(job => (
                  <div key={job.id} className="table-row">
                    <span>{format(parseISO(job.date), 'MMM d')}</span>
                    <span>{job.clientName}</span>
                    <span>{formatCurrency(job.price)}</span>
                    <span>{formatCurrency(job.laborCost)}</span>
                    <span className="labor-pct high">{job.laborPercent.toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== JOB REPORT ===== */}
      {activeReport === 'jobs' && (
        <div className="report-content">
          {/* KPI Cards */}
          <div className="kpi-grid">
            <div className="kpi-card primary">
              <div className="kpi-data">
                <span className="kpi-value">{jobReportData.total}</span>
                <span className="kpi-label">Total Jobs</span>
              </div>
            </div>
            <div className="kpi-card success">
              <div className="kpi-data">
                <span className="kpi-value">{jobReportData.completed}</span>
                <span className="kpi-label">Completed</span>
              </div>
            </div>
            <div className="kpi-card warning">
              <div className="kpi-data">
                <span className="kpi-value">{jobReportData.cancelled}</span>
                <span className="kpi-label">Cancelled</span>
              </div>
            </div>
            <div className="kpi-card">
              <div className="kpi-data">
                <span className="kpi-value">{jobReportData.completionRate.toFixed(0)}%</span>
                <span className="kpi-label">Completion Rate</span>
              </div>
            </div>
          </div>

          {/* On-Time Performance */}
          <div className="report-section">
            <h3>On-Time Performance</h3>
            <div className="performance-metrics">
              <div className="metric-card">
                <div className="metric-circle" style={{ '--percent': jobReportData.onTimeRate }}>
                  <span className="metric-value">{jobReportData.onTimeRate.toFixed(0)}%</span>
                </div>
                <span className="metric-label">On-Time Rate</span>
                <span className="metric-detail">{jobReportData.onTime} on-time, {jobReportData.late} late</span>
              </div>
              <div className="metric-card">
                <div className={`time-variance ${jobReportData.avgTimeVariance > 0 ? 'over' : 'under'}`}>
                  {jobReportData.avgTimeVariance > 0 ? <ArrowUp size={20} /> : <ArrowDown size={20} />}
                  <span>{Math.abs(jobReportData.avgTimeVariance).toFixed(0)} min</span>
                </div>
                <span className="metric-label">Avg Time Variance</span>
                <span className="metric-detail">{jobReportData.avgTimeVariance > 0 ? 'Over estimate' : 'Under estimate'}</span>
              </div>
            </div>
          </div>

          {/* Jobs by Day */}
          <div className="report-section">
            <h3>Jobs by Day of Week</h3>
            <div className="day-chart">
              {jobReportData.byDayOfWeek.map((count, idx) => {
                const max = Math.max(...jobReportData.byDayOfWeek, 1);
                const height = (count / max) * 100;
                return (
                  <div key={idx} className="day-bar">
                    <div className="day-value">{count}</div>
                    <div className="day-fill" style={{ height: `${height}%` }} />
                    <div className="day-label">{dayLabels[idx]}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Jobs by Service Type */}
          <div className="report-section">
            <h3>Jobs by Service Type</h3>
            <div className="data-table">
              <div className="table-header">
                <span>Service Type</span>
                <span>Total</span>
                <span>Completed</span>
                <span>Cancelled</span>
              </div>
              {Object.entries(jobReportData.byServiceType).map(([type, data]) => (
                <div key={type} className="table-row">
                  <span className="service-type">{serviceTypeLabels[type] || type}</span>
                  <span>{data.total}</span>
                  <span className="completed">{data.completed}</span>
                  <span className="cancelled">{data.cancelled}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===== PAYROLL REPORT ===== */}
      {activeReport === 'payroll' && (
        <div className="report-content">
          {/* KPI Cards */}
          <div className="kpi-grid">
            <div className="kpi-card primary">
              <div className="kpi-data">
                <span className="kpi-value">{formatCurrency(payrollData.totalGrossPay)}</span>
                <span className="kpi-label">Total Payroll</span>
              </div>
            </div>
            <div className="kpi-card">
              <div className="kpi-data">
                <span className="kpi-value">{formatHoursMinutes(payrollData.totalHours)}</span>
                <span className="kpi-label">Total Hours</span>
              </div>
            </div>
            <div className="kpi-card">
              <div className="kpi-data">
                <span className="kpi-value">{formatCurrency(payrollData.totalRegularPay)}</span>
                <span className="kpi-label">Regular Pay</span>
              </div>
            </div>
            <div className="kpi-card success">
              <div className="kpi-data">
                <span className="kpi-value">{formatCurrency(payrollData.totalTips)}</span>
                <span className="kpi-label">Total Tips</span>
              </div>
            </div>
          </div>

          {/* Export Button */}
          <div className="export-section">
            <button className="btn btn-primary" onClick={exportPayrollCSV}>
              <Download size={18} />
              Export for Gusto
            </button>
          </div>

          {/* Payroll Table */}
          <div className="report-section">
            <h3>Employee Payroll</h3>
            <div className="data-table payroll-table">
              <div className="table-header">
                <span>Employee</span>
                <span>Jobs</span>
                <span>Hours</span>
                <span>Rate</span>
                <span>Regular Pay</span>
                <span>Tips</span>
                <span>Gross Pay</span>
              </div>
              {payrollData.employees.map(emp => (
                <div key={emp.id} className="table-row">
                  <span className="employee-name">{emp.name}</span>
                  <span>{emp.jobs}</span>
                  <span>{formatHoursMinutes(emp.regularHours)}</span>
                  <span>{formatCurrency(emp.hourlyRate)}/hr</span>
                  <span>{formatCurrency(emp.regularPay)}</span>
                  <span className="tips">{formatCurrency(emp.tips)}</span>
                  <span className="gross-pay">{formatCurrency(emp.grossPay)}</span>
                </div>
              ))}
              <div className="table-row totals">
                <span>TOTAL</span>
                <span>-</span>
                <span>{formatHoursMinutes(payrollData.totalHours)}</span>
                <span>-</span>
                <span>{formatCurrency(payrollData.totalRegularPay)}</span>
                <span className="tips">{formatCurrency(payrollData.totalTips)}</span>
                <span className="gross-pay">{formatCurrency(payrollData.totalGrossPay)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== PAYROLL SPREADSHEET ===== */}
      {activeReport === 'payroll_spreadsheet' && (() => {
        const { employees, days, dayTotals, grandTotalHours, grandTotalPay } = payrollSpreadsheet;

        return (
          <div className="report-content">
            {/* Payroll Date Range Picker */}
            <div className="payroll-date-range">
              <div className="payroll-date-range-inputs">
                <Calendar size={16} />
                <label>From</label>
                <input
                  type="date"
                  value={payrollStartDate || format(startDate, 'yyyy-MM-dd')}
                  onChange={(e) => setPayrollStartDate(e.target.value)}
                />
                <label>To</label>
                <input
                  type="date"
                  value={payrollEndDate || format(endDate, 'yyyy-MM-dd')}
                  onChange={(e) => setPayrollEndDate(e.target.value)}
                />
                {(payrollStartDate || payrollEndDate) && (
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => { setPayrollStartDate(''); setPayrollEndDate(''); }}
                    title="Reset to global date range"
                  >
                    <X size={14} /> Reset
                  </button>
                )}
              </div>
              <div className="payroll-date-range-display">
                {format(payrollEffectiveStart, 'MMM d, yyyy')} - {format(payrollEffectiveEnd, 'MMM d, yyyy')}
                {payrollTimeRecords.length > 0 && (
                  <span className="payroll-hours-source"> (Actual clocked hours)</span>
                )}
                {payrollTimeRecords.length === 0 && (
                  <span className="payroll-hours-source fallback"> (Job hours - no time records found)</span>
                )}
              </div>
            </div>

            {/* KPI Cards */}
            <div className="kpi-grid">
              <div className="kpi-card primary">
                <div className="kpi-data">
                  <span className="kpi-value">{formatCurrency(grandTotalPay)}</span>
                  <span className="kpi-label">Total Payroll</span>
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-data">
                  <span className="kpi-value">{formatHoursMinutes(grandTotalHours)}</span>
                  <span className="kpi-label">Total Hours</span>
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-data">
                  <span className="kpi-value">{employees.length}</span>
                  <span className="kpi-label">Employees</span>
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-data">
                  <span className="kpi-value">{days.length}</span>
                  <span className="kpi-label">Days in Range</span>
                </div>
              </div>
            </div>

            {/* Export Button */}
            <div className="export-section">
              <button className="btn btn-primary" onClick={exportPayrollSpreadsheetCSV}>
                <Download size={18} />
                Export Spreadsheet CSV
              </button>
            </div>

            {/* Spreadsheet */}
            <div className="report-section">
              <h3><Grid size={18} /> Actual Hours by Day</h3>
              <div className="payroll-spreadsheet-wrap">
                <div className="payroll-spreadsheet">
                  <table className="ps-table">
                    <thead>
                      <tr>
                        <th className="ps-sticky-col ps-header-emp">Employee</th>
                        <th className="ps-header-rate">Rate</th>
                        {days.map(day => {
                          const dayStr = format(day, 'yyyy-MM-dd');
                          const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                          return (
                            <th key={dayStr} className={`ps-header-day ${isWeekend ? 'ps-weekend' : ''}`}>
                              <span className="ps-day-name">{format(day, 'EEE')}</span>
                              <span className="ps-day-date">{format(day, 'M/d')}</span>
                            </th>
                          );
                        })}
                        <th className="ps-header-total">Total Hrs</th>
                        <th className="ps-header-total">Total Pay</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees.map(emp => (
                        <tr key={emp.id}>
                          <td className="ps-sticky-col ps-emp-name">{emp.name}</td>
                          <td className="ps-rate">{formatCurrency(emp.hourlyRate)}</td>
                          {days.map(day => {
                            const dayStr = format(day, 'yyyy-MM-dd');
                            const hours = emp.days[dayStr] || 0;
                            const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                            return (
                              <td key={dayStr} className={`ps-cell ${hours > 0 ? 'ps-has-hours' : ''} ${isWeekend ? 'ps-weekend' : ''}`}>
                                {hours > 0 ? formatHoursMinutes(hours) : ''}
                              </td>
                            );
                          })}
                          <td className="ps-row-total">{formatHoursMinutes(emp.totalHours)}</td>
                          <td className="ps-row-pay">{formatCurrency(emp.totalPay)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="ps-totals-row">
                        <td className="ps-sticky-col ps-totals-label">TOTAL</td>
                        <td className="ps-rate"></td>
                        {days.map(day => {
                          const dayStr = format(day, 'yyyy-MM-dd');
                          const total = dayTotals[dayStr] || 0;
                          return (
                            <td key={dayStr} className={`ps-col-total ${total > 0 ? 'ps-has-hours' : ''}`}>
                              {total > 0 ? formatHoursMinutes(total) : ''}
                            </td>
                          );
                        })}
                        <td className="ps-grand-total">{formatHoursMinutes(grandTotalHours)}</td>
                        <td className="ps-grand-pay">{formatCurrency(grandTotalPay)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              {employees.length === 0 && (
                <div className="empty-report">No payroll data for this date range.</div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ===== WEEKLY PAY REPORT ===== */}
      {activeReport === 'weekly_pay' && (() => {
        const { weeks, grandTotalHours, grandGrossPay } = weeklyPayReport;

        return (
          <div className="report-content">
            {/* Payroll Date Range Picker (reuses same state as payroll grid) */}
            <div className="payroll-date-range">
              <div className="payroll-date-range-inputs">
                <Calendar size={16} />
                <label>From</label>
                <input
                  type="date"
                  value={payrollStartDate || format(startDate, 'yyyy-MM-dd')}
                  onChange={(e) => setPayrollStartDate(e.target.value)}
                />
                <label>To</label>
                <input
                  type="date"
                  value={payrollEndDate || format(endDate, 'yyyy-MM-dd')}
                  onChange={(e) => setPayrollEndDate(e.target.value)}
                />
                {(payrollStartDate || payrollEndDate) && (
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => { setPayrollStartDate(''); setPayrollEndDate(''); }}
                    title="Reset to global date range"
                  >
                    <X size={14} /> Reset
                  </button>
                )}
              </div>
              <div className="payroll-date-range-display">
                {format(payrollEffectiveStart, 'MMM d, yyyy')} - {format(payrollEffectiveEnd, 'MMM d, yyyy')}
                <span style={{ marginLeft: 8, color: '#64748b' }}>({weeks.length} week{weeks.length !== 1 ? 's' : ''})</span>
              </div>
            </div>

            {/* KPI Cards */}
            <div className="kpi-grid">
              <div className="kpi-card primary">
                <div className="kpi-data">
                  <span className="kpi-value">{formatCurrency(grandGrossPay)}</span>
                  <span className="kpi-label">Total Gross Pay</span>
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-data">
                  <span className="kpi-value">{formatHoursMinutes(grandTotalHours)}</span>
                  <span className="kpi-label">Total Hours</span>
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-data">
                  <span className="kpi-value">{weeks.length}</span>
                  <span className="kpi-label">Weeks</span>
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-data">
                  <span className="kpi-value">
                    {new Set(weeks.flatMap(w => w.employees.map(e => e.id))).size}
                  </span>
                  <span className="kpi-label">Employees</span>
                </div>
              </div>
            </div>

            {/* Export Buttons */}
            <div className="export-section">
              <button className="btn btn-primary" onClick={exportWeeklyPayCSV}>
                <Download size={18} />
                Export Weekly Pay CSV
              </button>
              <button
                className="btn btn-outline"
                onClick={() => openWeeklyPayPdf({
                  weeks,
                  grandTotalHours,
                  grandGrossPay,
                  dateRangeLabel: `${format(payrollEffectiveStart, 'MMM d, yyyy')} – ${format(payrollEffectiveEnd, 'MMM d, yyyy')}`
                })}
              >
                <FileText size={18} />
                Export Weekly Pay PDF
              </button>
            </div>

            {/* Per-Week Breakdown */}
            {weeks.map((week, weekIdx) => (
              <div key={weekIdx} className="report-section wp-week-section">
                <div className="wp-week-header">
                  <h3><Calendar size={18} /> {week.label}</h3>
                  <div className="wp-week-totals">
                    <span className="wp-week-hours">{formatHoursMinutes(week.totalHours)} hrs</span>
                    <span className="wp-week-pay">{formatCurrency(week.grossPay)}</span>
                  </div>
                </div>

                {week.employees.length === 0 ? (
                  <div className="empty-report">No hours recorded this week.</div>
                ) : (
                  <div className="wp-employee-list">
                    {week.employees.map(emp => (
                      <div key={emp.id} className="wp-employee-card">
                        <div className="wp-emp-header">
                          <span className="wp-emp-name">{emp.name}</span>
                          <div className="wp-emp-summary">
                            <span className="wp-emp-hours">{formatHoursMinutes(emp.totalHours)} hrs</span>
                            <span className="wp-emp-gross">{formatCurrency(emp.grossPay)}</span>
                          </div>
                        </div>
                        {emp.rateBreakdown.length > 0 && (
                          <div className="wp-rate-breakdown">
                            {emp.rateBreakdown.map((rb, rbIdx) => (
                              <div key={rbIdx} className="wp-rate-row">
                                <span className="wp-rate-label">{rb.label}</span>
                                <span className="wp-rate-value">{formatCurrency(rb.rate)}/hr</span>
                                <span className="wp-rate-hours">{formatHoursMinutes(rb.hours)}</span>
                                <span className="wp-rate-pay">{formatCurrency(rb.pay)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Grand Totals */}
            {weeks.length > 1 && (
              <div className="report-section wp-grand-totals">
                <div className="wp-week-header grand">
                  <h3>Grand Total</h3>
                  <div className="wp-week-totals">
                    <span className="wp-week-hours">{formatHoursMinutes(grandTotalHours)} hrs</span>
                    <span className="wp-week-pay">{formatCurrency(grandGrossPay)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ===== JOB LOG ===== */}
      {activeReport === 'joblog' && (() => {
        // Safe date parser for Firestore timestamps
        const safeParse = (val) => {
          if (!val) return null;
          if (val.toDate) return val.toDate();
          if (val instanceof Date) return val;
          if (typeof val === 'string') { try { return parseISO(val); } catch { return null; } }
          return null;
        };
        const safeFmt = (val, fmt) => {
          const d = safeParse(val);
          return d ? format(d, fmt) : '—';
        };
        
        const completedJobs = filteredJobs
          .filter(j => j.status === 'completed')
          .sort((a, b) => {
            const da = safeParse(a.completedAt) || safeParse(a.date) || new Date(0);
            const db2 = safeParse(b.completedAt) || safeParse(b.date) || new Date(0);
            return db2 - da;
          });

        const totalCompleted = completedJobs.length;
        const totalRevenue = completedJobs.reduce((s, j) => s + (j.price || 0), 0);
        const totalLabor = completedJobs.reduce((s, j) => s + (j.laborCost || 0), 0);
        const avgDuration = totalCompleted > 0 
          ? completedJobs.reduce((s, j) => s + (j.actual_duration_min || 0), 0) / totalCompleted 
          : 0;
        const withPhotos = completedJobs.filter(j => j.photos?.length > 0).length;

        return (
          <div className="report-content">
            {/* KPI Cards */}
            <div className="kpi-grid">
              <div className="kpi-card primary">
                <div className="kpi-data">
                  <span className="kpi-value">{totalCompleted}</span>
                  <span className="kpi-label">Completed Jobs</span>
                </div>
              </div>
              <div className="kpi-card success">
                <div className="kpi-data">
                  <span className="kpi-value">{formatCurrency(totalRevenue)}</span>
                  <span className="kpi-label">Revenue</span>
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-data">
                  <span className="kpi-value">{Math.round(avgDuration)} min</span>
                  <span className="kpi-label">Avg Duration</span>
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-data">
                  <span className="kpi-value">{withPhotos}/{totalCompleted}</span>
                  <span className="kpi-label">With Photos</span>
                </div>
              </div>
            </div>

            {/* Job List */}
            <div className="report-section">
              <h3><ClipboardList size={18} /> Completed Job Log</h3>
              {completedJobs.length === 0 ? (
                <div className="empty-report">No completed jobs in this date range.</div>
              ) : (
                <div className="job-log-list">
                  {completedJobs.map(job => {
                    const isExpanded = expandedJobId === job.id;
                    const photos = job.photos || [];
                    const beforePhotos = photos.filter(p => p.type === 'before');
                    const afterPhotos = photos.filter(p => p.type === 'after');
                    const damagePhotos = photos.filter(p => p.type?.startsWith('damage'));
                    const team = job.teamNames || job.team || [];
                    const teamStr = Array.isArray(team) 
                      ? team.map(t => {
                          if (typeof t === 'string' && t.startsWith('emp')) {
                            const emp = state.employees.find(e => e.id === t);
                            return emp ? `${emp.firstName} ${emp.lastName?.[0] || ''}`.trim() : t;
                          }
                          return t;
                        }).join(', ')
                      : '';
                    // Build helper string
                    const helpers = job.helperNames || [];
                    const helperStr = helpers.length > 0 ? helpers.join(', ') : '';
                    const durationMin = job.actual_duration_min || 0;
                    const hrs = Math.floor(durationMin / 60);
                    const mins = durationMin % 60;
                    const durationStr = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;

                    // Payment status
                    const jobInvoice = getJobInvoice(job.id);
                    let paymentStatus = 'unbilled';
                    let paymentLabel = 'Unbilled';
                    if (jobInvoice) {
                      if (jobInvoice.status === 'paid') {
                        paymentStatus = 'paid';
                        paymentLabel = 'Paid';
                      } else if (jobInvoice.status === 'sent' || jobInvoice.status === 'pending') {
                        const dueDate = jobInvoice.dueDate ? new Date(jobInvoice.dueDate) : null;
                        if (dueDate && dueDate < new Date()) {
                          paymentStatus = 'pastdue';
                          paymentLabel = 'Past Due';
                        } else {
                          paymentStatus = 'billed';
                          paymentLabel = 'Billed';
                        }
                      } else if (jobInvoice.status === 'draft') {
                        paymentStatus = 'draft';
                        paymentLabel = 'Draft';
                      }
                    }

                    return (
                      <div key={job.id} className={`job-log-card ${isExpanded ? 'expanded' : ''}`}>
                        <div className="job-log-header" onClick={() => setExpandedJobId(isExpanded ? null : job.id)}>
                          <div className="job-log-left">
                            <div className="job-log-date">{safeFmt(job.date, 'EEE, MMM d')}</div>
                            <div className="job-log-client">{job.clientName || 'Unknown'}</div>
                            <div className="job-log-address"><MapPin size={12} /> {job.address || '—'}</div>
                            {teamStr && <div className="job-log-team"><Users size={11} /> {teamStr}</div>}
                            {helperStr && <div className="job-log-helpers">+ Helper: {helperStr}</div>}
                          </div>
                          <div className="job-log-right">
                            <div className="job-log-price">{formatCurrency(job.price || 0)}</div>
                            <span className={`job-payment-badge ${paymentStatus}`}>{paymentLabel}</span>
                            <div className="job-log-duration"><Clock size={12} /> {durationStr}</div>
                            {photos.length > 0 && <div className="job-log-photo-badge"><Camera size={12} /> {photos.length}</div>}
                          </div>
                          <ChevronRight size={18} className={`job-log-chevron ${isExpanded ? 'rotated' : ''}`} />
                        </div>

                        {isExpanded && (
                          <div className="job-log-detail">
                            {/* Timeline */}
                            <div className="job-log-timeline">
                              <div className="timeline-header-row">
                                <h4>Timeline</h4>
                                {job.timeOverride && (
                                  <span className="time-override-badge" title={`Overridden: ${job.timeOverrideNote || ''}`}>
                                    <Edit2 size={10} /> Edited
                                  </span>
                                )}
                                <button 
                                  className="time-edit-btn"
                                  onClick={(e) => { e.stopPropagation(); openTimeEdit(job); }}
                                  title="Edit job times"
                                >
                                  <Edit2 size={12} /> Edit Times
                                </button>
                              </div>

                              {/* Time Edit Form */}
                              {editingTimeJobId === job.id && (
                                <div className="time-edit-form" onClick={e => e.stopPropagation()}>
                                  <div className="time-edit-row">
                                    <div className="time-edit-field">
                                      <label>Start Time</label>
                                      <input 
                                        type="time" 
                                        value={timeEditStart} 
                                        onChange={e => setTimeEditStart(e.target.value)}
                                      />
                                    </div>
                                    <div className="time-edit-field">
                                      <label>End Time</label>
                                      <input 
                                        type="time" 
                                        value={timeEditEnd} 
                                        onChange={e => setTimeEditEnd(e.target.value)}
                                      />
                                    </div>
                                    {timeEditStart && timeEditEnd && (
                                      <div className="time-edit-preview">
                                        {(() => {
                                          const s = new Date(`2026-01-01T${timeEditStart}:00`);
                                          const e2 = new Date(`2026-01-01T${timeEditEnd}:00`);
                                          const mins = Math.round((e2 - s) / 60000);
                                          if (mins <= 0) return <span className="preview-bad">Invalid</span>;
                                          const h = Math.floor(mins / 60);
                                          const m = mins % 60;
                                          return <span className="preview-good">{h > 0 ? `${h}h ${m}m` : `${m}m`}</span>;
                                        })()}
                                      </div>
                                    )}
                                  </div>
                                  <div className="time-edit-note-row">
                                    <label>Reason for override <span className="required">*</span></label>
                                    <input 
                                      type="text"
                                      placeholder="e.g. Forgot to hit Start — arrived at 9:15 AM"
                                      value={timeEditNote}
                                      onChange={e => setTimeEditNote(e.target.value)}
                                      className="time-edit-note-input"
                                    />
                                  </div>
                                  {job.timeOverride && job.original_actual_start && (
                                    <div className="time-edit-original">
                                      Original: {safeFmt(job.original_actual_start, 'h:mm a')} → {safeFmt(job.original_actual_end, 'h:mm a')}
                                      {job.original_actual_duration_min != null && ` (${Math.floor(job.original_actual_duration_min / 60)}h ${job.original_actual_duration_min % 60}m)`}
                                    </div>
                                  )}
                                  <div className="time-edit-actions">
                                    <button className="btn-cancel" onClick={() => setEditingTimeJobId(null)}>Cancel</button>
                                    <button 
                                      className="btn-save-time" 
                                      onClick={() => saveTimeOverride(job)}
                                      disabled={savingTimeEdit || !timeEditNote.trim()}
                                    >
                                      <Save size={13} /> {savingTimeEdit ? 'Saving...' : 'Save Override'}
                                    </button>
                                  </div>
                                </div>
                              )}

                              {/* Override note if present */}
                              {job.timeOverride && job.timeOverrideNote && editingTimeJobId !== job.id && (
                                <div className="time-override-note">
                                  <AlertTriangle size={12} /> <strong>Override:</strong> {job.timeOverrideNote}
                                  {job.timeOverrideAt && <span className="override-timestamp"> — {safeFmt(job.timeOverrideAt, 'MMM d, h:mm a')}</span>}
                                </div>
                              )}

                              <div className="timeline-steps">
                                {job.enRouteAt && (
                                  <div className="timeline-step">
                                    <div className="timeline-icon en-route"><Navigation size={14} /></div>
                                    <div className="timeline-info">
                                      <span className="timeline-label">En Route</span>
                                      <span className="timeline-time">{safeFmt(job.enRouteAt, 'h:mm a')}</span>
                                    </div>
                                  </div>
                                )}
                                {job.actual_start && (
                                  <div className="timeline-step">
                                    <div className={`timeline-icon started ${job.timeOverride ? 'overridden' : ''}`}><Play size={14} /></div>
                                    <div className="timeline-info">
                                      <span className="timeline-label">Started {job.timeOverride ? '(edited)' : ''}</span>
                                      <span className="timeline-time">{safeFmt(job.actual_start, 'h:mm a')}</span>
                                    </div>
                                  </div>
                                )}
                                {(job.completedAt || job.actual_end) && (
                                  <div className="timeline-step">
                                    <div className={`timeline-icon completed ${job.timeOverride ? 'overridden' : ''}`}><Flag size={14} /></div>
                                    <div className="timeline-info">
                                      <span className="timeline-label">Completed {job.timeOverride ? '(edited)' : ''}</span>
                                      <span className="timeline-time">{safeFmt(job.completedAt || job.actual_end, 'h:mm a')}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Details grid */}
                            <div className="job-log-details-grid">
                              <div className="detail-item">
                                <span className="detail-label">Team</span>
                                <span className="detail-value">{teamStr || '—'}</span>
                              </div>
                              <div className="detail-item">
                                <span className="detail-label">Service</span>
                                <span className="detail-value">{job.serviceType?.replace(/_/g, ' ') || job.frequency || '—'}</span>
                              </div>
                              <div className="detail-item">
                                <span className="detail-label">Duration</span>
                                <span className="detail-value">{durationStr}</span>
                              </div>
                              <div className="detail-item">
                                <span className="detail-label">Scheduled</span>
                                <span className="detail-value">{job.scheduledTime || '—'}{job.estimatedDuration ? ` (est ${job.estimatedDuration} min)` : ''}</span>
                              </div>
                              {job.laborCost > 0 && (
                                <div className="detail-item">
                                  <span className="detail-label">Labor Cost</span>
                                  <span className="detail-value">{formatCurrency(job.laborCost)} ({job.laborPercent || 0}%)</span>
                                </div>
                              )}
                              {job.geofenceVerified !== undefined && (
                                <div className="detail-item">
                                  <span className="detail-label">Geofence</span>
                                  <span className={`detail-value ${job.geofenceVerified ? 'verified' : 'flagged'}`}>
                                    {job.geofenceVerified ? '✓ Verified' : '⚠ Not verified'}
                                  </span>
                                </div>
                              )}
                              {job.clientNotifiedOnMyWay && (
                                <div className="detail-item">
                                  <span className="detail-label">Client Notified</span>
                                  <span className="detail-value">✓ On My Way sent {safeFmt(job.clientNotifiedOnMyWayAt, 'h:mm a')}</span>
                                </div>
                              )}
                            </div>

                            {/* Photos */}
                            {photos.length > 0 && (
                              <div className="job-log-photos">
                                <h4><Camera size={16} /> Photos ({photos.length})</h4>
                                {beforePhotos.length > 0 && (
                                  <div className="photo-group">
                                    <span className="photo-group-label">Before ({beforePhotos.length})</span>
                                    <div className="photo-thumbs">
                                      {beforePhotos.map((p, i) => (
                                        <div key={p.id || i} className="photo-thumb" onClick={() => setPhotoViewer(p)}>
                                          <img src={p.url} alt={`Before ${i + 1}`} loading="lazy" />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {afterPhotos.length > 0 && (
                                  <div className="photo-group">
                                    <span className="photo-group-label">After ({afterPhotos.length})</span>
                                    <div className="photo-thumbs">
                                      {afterPhotos.map((p, i) => (
                                        <div key={p.id || i} className="photo-thumb" onClick={() => setPhotoViewer(p)}>
                                          <img src={p.url} alt={`After ${i + 1}`} loading="lazy" />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {damagePhotos.length > 0 && (
                                  <div className="photo-group damage">
                                    <span className="photo-group-label">Damage ({damagePhotos.length})</span>
                                    <div className="photo-thumbs">
                                      {damagePhotos.map((p, i) => (
                                        <div key={p.id || i} className="photo-thumb" onClick={() => setPhotoViewer(p)}>
                                          <img src={p.url} alt={`Damage ${i + 1}`} loading="lazy" />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Damage Reports */}
                            {job.damageReports?.length > 0 && (
                              <div className="job-log-damage">
                                <h4><AlertCircle size={16} /> Damage Reports</h4>
                                {job.damageReports.map((dr, i) => (
                                  <div key={dr.id || i} className="damage-report-item">
                                    <span className={`damage-type ${dr.type}`}>{dr.type === 'existing' ? 'Pre-existing' : 'New Damage'}</span>
                                    <p>{dr.description}</p>
                                    <span className="damage-time">Reported {safeFmt(dr.reportedAt, 'h:mm a')}</span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Notes */}
                            {(job.notes || job.teamNotes || job.specialInstructions) && (
                              <div className="job-log-notes">
                                <h4>Notes</h4>
                                {job.specialInstructions && <p className="note-text"><strong>Instructions:</strong> {job.specialInstructions}</p>}
                                {job.teamNotes && <p className="note-text"><strong>Team:</strong> {job.teamNotes}</p>}
                                {job.notes && <p className="note-text">{job.notes}</p>}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Photo Viewer Modal */}

      {/* ===== TIME ANALYSIS TAB ===== */}
      {activeReport === 'timeanalysis' && (() => {
        const today = new Date();
        const todayStr = format(today, 'yyyy-MM-dd');

        // Build employee rate map
        const empMap = {};
        state.employees.forEach(emp => {
          empMap[emp.id] = { id: emp.id, name: `${emp.firstName || ''} ${emp.lastName || ''}`.trim(), firstName: emp.firstName, rate: emp.hourlyRate || emp.payRate || 15, role: emp.role || 'employee' };
        });

        // Safe date parser
        const safeParse = (val) => {
          if (!val) return null;
          if (val.toDate) return val.toDate();
          if (val instanceof Date) return val;
          if (typeof val === 'string') { try { return parseISO(val); } catch { return null; } }
          return null;
        };

        // Get completed jobs in range
        const completedJobs = filteredJobs.filter(j => j.status === 'completed');

        // Build per-employee, per-date stats
        // For each date: group jobs by route, calc job time + drive time per employee
        const dateSet = new Set(completedJobs.map(j => j.date).filter(Boolean));
        const dates = [...dateSet].sort();

        // Group jobs by date and route
        const jobsByDateRoute = {};
        completedJobs.forEach(job => {
          const d = job.date;
          const r = job.routeId || 'unassigned';
          if (!jobsByDateRoute[d]) jobsByDateRoute[d] = {};
          if (!jobsByDateRoute[d][r]) jobsByDateRoute[d][r] = { jobs: [], team: new Set(), routeName: job.routeName };
          jobsByDateRoute[d][r].jobs.push(job);
          (job.team || []).forEach(id => jobsByDateRoute[d][r].team.add(id));
          (job.helpers || []).forEach(id => jobsByDateRoute[d][r].team.add(id));
        });

        // Per employee accumulator: { empId: { dates: { [date]: { jobMin, driveMin, clockedMin } }, ... } }
        const empStats = {};

        // === PER-EMPLOYEE TIMELINE APPROACH ===
        // For each date, build each employee's personal timeline of jobs across all routes
        // This correctly accounts for helper drives between routes

        dates.forEach(dateStr => {
          // Collect ALL jobs each employee is on (as team or helper)
          const dayJobs = completedJobs.filter(j => j.date === dateStr);
          const empJobsMap = {}; // { empId: [ job, job, ... ] }

          dayJobs.forEach(job => {
            const allWorkers = [...new Set([...(job.team || []), ...(job.helpers || [])])];
            allWorkers.forEach(empId => {
              if (!empJobsMap[empId]) empJobsMap[empId] = [];
              empJobsMap[empId].push({
                ...job,
                isHelper: (job.helpers || []).includes(empId)
              });
            });
          });

          // For each employee, sort their jobs chronologically and calculate their personal timeline
          Object.entries(empJobsMap).forEach(([empId, empJobs]) => {
            // Sort by actual_start time
            const sorted = [...empJobs].sort((a, b) => {
              const ta = safeParse(a.actual_start) || safeParse(a.scheduledTime) || new Date(0);
              const tb = safeParse(b.actual_start) || safeParse(b.scheduledTime) || new Date(0);
              return ta - tb;
            });

            let personJobMin = 0;
            let personDriveMin = 0;

            sorted.forEach((job, idx) => {
              // Job time: each person works the FULL job duration (not split)
              // If Ashanti and Brandi clean a 3hr job together, each worked 3 hrs
              const jobWorkers = [...new Set([...(job.team || []), ...(job.helpers || [])])];
              const jobMin = job.actual_duration_min || (job.duration ? job.duration * 60 : 0);
              personJobMin += jobMin;

              // Drive time: based on THIS employee's personal timeline
              if (idx === 0) {
                // First job of their day
                const enRoute = safeParse(job.enRouteAt);
                const started = safeParse(job.actual_start);
                if (enRoute && started) {
                  personDriveMin += Math.max(0, Math.min(60, (started - enRoute) / 60000));
                } else {
                  personDriveMin += 12;
                }
              } else {
                // Gap from their previous job's completion to this job's start
                const prevJob = sorted[idx - 1];
                const prevEnd = safeParse(prevJob.completedAt) || safeParse(prevJob.actual_end);
                const thisStart = safeParse(job.actual_start);
                if (prevEnd && thisStart) {
                  const gap = (thisStart - prevEnd) / 60000;
                  // Cross-route helper drive: could be longer, allow up to 45 min
                  const isHelperDrive = job.isHelper || prevJob.isHelper;
                  const maxDrive = isHelperDrive ? 45 : 30;
                  personDriveMin += Math.max(0, Math.min(maxDrive, gap));
                } else {
                  // If they're going to help on another route, estimate longer drive
                  personDriveMin += job.isHelper ? 20 : 15;
                }
              }
            });

            // Store
            if (!empStats[empId]) empStats[empId] = { dates: {} };
            if (!empStats[empId].dates[dateStr]) empStats[empId].dates[dateStr] = { jobMin: 0, driveMin: 0, clockedMin: 0 };
            empStats[empId].dates[dateStr].jobMin += personJobMin;
            empStats[empId].dates[dateStr].driveMin += personDriveMin;
          });

          // Get clocked hours from time records for this date
          const dayRecords = taTimeRecords.filter(r => r.date === dateStr);
          dayRecords.forEach(rec => {
            if (rec.employeeId && rec.hoursWorked) {
              if (!empStats[rec.employeeId]) empStats[rec.employeeId] = { dates: {} };
              if (!empStats[rec.employeeId].dates[dateStr]) empStats[rec.employeeId].dates[dateStr] = { jobMin: 0, driveMin: 0, clockedMin: 0 };
              empStats[rec.employeeId].dates[dateStr].clockedMin = rec.hoursWorked * 60;
            }
          });
        });

        // Build final leaderboard
        const leaderboard = Object.entries(empStats).map(([empId, data]) => {
          const emp = empMap[empId];
          if (!emp) return null;
          // Exclude managers from downtime report
          if (emp.role === 'manager' || emp.role === 'admin' || emp.role === 'owner') return null;

          let totalJobMin = 0, totalDriveMin = 0, totalDownMin = 0, totalClockedMin = 0;
          const dayBreakdown = [];

          Object.entries(data.dates).sort(([a], [b]) => a.localeCompare(b)).forEach(([dateStr, day]) => {
            const clocked = day.clockedMin || (day.jobMin + day.driveMin) * 1.15; // fallback: estimate clocked as 115% of productive
            const down = Math.max(0, clocked - day.jobMin - day.driveMin);
            totalJobMin += day.jobMin;
            totalDriveMin += day.driveMin;
            totalDownMin += down;
            totalClockedMin += clocked;
            dayBreakdown.push({ date: dateStr, label: format(parseISO(dateStr), 'EEE, MMM d'), jobMin: day.jobMin, driveMin: day.driveMin, downMin: down, clockedMin: clocked });
          });

          return {
            empId, name: emp.name, firstName: emp.firstName, rate: emp.rate,
            totalJobMin, totalDriveMin, totalDownMin, totalClockedMin,
            daysWorked: dayBreakdown.length, dayBreakdown,
            jobPct: totalClockedMin > 0 ? Math.round(totalJobMin / totalClockedMin * 100) : 0,
            drivePct: totalClockedMin > 0 ? Math.round(totalDriveMin / totalClockedMin * 100) : 0,
            downPct: totalClockedMin > 0 ? Math.round(totalDownMin / totalClockedMin * 100) : 0,
            downCost: totalDownMin / 60 * emp.rate
          };
        }).filter(Boolean).sort((a, b) => b.totalDownMin - a.totalDownMin);

        // Team totals
        const totals = leaderboard.reduce((acc, e) => ({
          job: acc.job + e.totalJobMin, drive: acc.drive + e.totalDriveMin,
          down: acc.down + e.totalDownMin, clocked: acc.clocked + e.totalClockedMin,
          downCost: acc.downCost + e.downCost
        }), { job: 0, drive: 0, down: 0, clocked: 0, downCost: 0 });

        const tJobPct = totals.clocked > 0 ? Math.round(totals.job / totals.clocked * 100) : 0;
        const tDrivePct = totals.clocked > 0 ? Math.round(totals.drive / totals.clocked * 100) : 0;
        const tDownPct = totals.clocked > 0 ? Math.round(totals.down / totals.clocked * 100) : 0;
        const totalDaysWorked = leaderboard.reduce((s, e) => s + e.daysWorked, 0);
        const avgDownPerDay = totalDaysWorked > 0 ? Math.round(totals.down / totalDaysWorked) : 0;

        const fmtMin = (min) => { const h = Math.floor(min / 60); const m = Math.round(min % 60); return h > 0 ? `${h}h ${m}m` : `${m}m`; };

        return (
          <div className="report-content">
            {/* KPI Cards */}
            <div className="kpi-grid">
              <div className="kpi-card" style={{ borderLeft: '4px solid var(--sage)' }}>
                <div className="kpi-data">
                  <span className="kpi-value" style={{ color: 'var(--sage)' }}>{tJobPct}%</span>
                  <span className="kpi-label">Job Time</span>
                  <span className="kpi-sub">{fmtMin(totals.job)} total</span>
                </div>
              </div>
              <div className="kpi-card" style={{ borderLeft: '4px solid #6366f1' }}>
                <div className="kpi-data">
                  <span className="kpi-value" style={{ color: '#6366f1' }}>{tDrivePct}%</span>
                  <span className="kpi-label">Drive Time</span>
                  <span className="kpi-sub">{fmtMin(totals.drive)} total</span>
                </div>
              </div>
              <div className="kpi-card" style={{ borderLeft: '4px solid #f59e0b' }}>
                <div className="kpi-data">
                  <span className="kpi-value" style={{ color: '#f59e0b' }}>{tDownPct}%</span>
                  <span className="kpi-label">Downtime</span>
                  <span className="kpi-sub">{fmtMin(totals.down)} · -{formatCurrency(totals.downCost).replace('.00', '')}</span>
                </div>
              </div>
              <div className="kpi-card" style={{ borderLeft: '4px solid var(--navy)' }}>
                <div className="kpi-data">
                  <span className="kpi-value">{fmtMin(avgDownPerDay)}</span>
                  <span className="kpi-label">Avg Downtime / Day</span>
                  <span className="kpi-sub">per person</span>
                </div>
              </div>
            </div>

            {/* Downtime Leaderboard */}
            <div className="report-section">
              <h3><AlertTriangle size={18} /> Downtime by Employee <span className="ta-sort-label">sorted by most downtime</span></h3>

              {/* Column headers */}
              <div className="ta-header-row">
                <span className="ta-col-rank">#</span>
                <span className="ta-col-name">Employee</span>
                <span className="ta-col-num">Job Time</span>
                <span className="ta-col-num">Drive Time</span>
                <span className="ta-col-num">Downtime</span>
                <span className="ta-col-num">Down Cost</span>
              </div>

              {leaderboard.length === 0 && <div className="empty-report">No completed jobs with team data in this range.</div>}

              {leaderboard.map((person, idx) => {
                const isExpanded = taExpandedEmp === person.empId;
                return (
                  <div key={person.empId} className={`ta-row-wrap ${idx === 0 ? 'worst' : ''}`}>
                    <div className={`ta-row ${isExpanded ? 'expanded' : ''}`} onClick={() => setTaExpandedEmp(isExpanded ? null : person.empId)}>
                      {/* Rank */}
                      <div className={`ta-col-rank ta-rank-${Math.min(idx, 3)}`}>
                        {idx === 0 ? <AlertTriangle size={14} /> : idx + 1}
                      </div>

                      {/* Name + bar */}
                      <div className="ta-col-name">
                        <div className="ta-name-line">
                          <span className="ta-emp-name">{person.name}</span>
                          <span className="ta-emp-meta">{person.daysWorked}d · ${person.rate}/hr</span>
                        </div>
                        <div className="ta-stacked-bar">
                          <div className="ta-bar-job" style={{ width: `${person.jobPct}%` }} />
                          <div className="ta-bar-drive" style={{ width: `${person.drivePct}%` }} />
                          <div className="ta-bar-down" style={{ width: `${person.downPct}%` }} />
                        </div>
                      </div>

                      <div className="ta-col-num">
                        <span className="ta-val sage">{fmtMin(person.totalJobMin)}</span>
                        <span className="ta-pct">{person.jobPct}%</span>
                      </div>
                      <div className="ta-col-num">
                        <span className="ta-val purple">{fmtMin(person.totalDriveMin)}</span>
                        <span className="ta-pct">{person.drivePct}%</span>
                      </div>
                      <div className="ta-col-num">
                        <span className={`ta-val ${person.downPct > 20 ? 'gold' : 'muted'}`}>{fmtMin(person.totalDownMin)}</span>
                        <span className="ta-pct">{person.downPct}%</span>
                      </div>
                      <div className="ta-col-num">
                        <span className={`ta-val ${person.downCost > 30 ? 'red' : 'muted'}`}>-{formatCurrency(person.downCost).replace('.00', '')}</span>
                      </div>
                    </div>

                    {/* Expanded: daily breakdown */}
                    {isExpanded && (
                      <div className="ta-expanded">
                        <div className="ta-expanded-title">Daily Breakdown</div>
                        {person.dayBreakdown.map((day, di) => {
                          const dJobPct = day.clockedMin > 0 ? Math.round(day.jobMin / day.clockedMin * 100) : 0;
                          const dDrivePct = day.clockedMin > 0 ? Math.round(day.driveMin / day.clockedMin * 100) : 0;
                          const dDownPct = day.clockedMin > 0 ? Math.round(day.downMin / day.clockedMin * 100) : 0;
                          const dayCost = day.downMin / 60 * person.rate;
                          return (
                            <div key={di} className="ta-day-row">
                              <span className="ta-day-label">{day.label}</span>
                              <div className="ta-day-bar">
                                <div className="ta-bar-job" style={{ width: `${dJobPct}%` }} />
                                <div className="ta-bar-drive" style={{ width: `${dDrivePct}%` }} />
                                <div className="ta-bar-down" style={{ width: `${dDownPct}%` }} />
                              </div>
                              <span className="ta-day-val sage">{fmtMin(day.jobMin)}</span>
                              <span className="ta-day-val purple">{fmtMin(day.driveMin)}</span>
                              <span className="ta-day-val gold">{fmtMin(day.downMin)}</span>
                              <span className="ta-day-val red">-${dayCost.toFixed(0)}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Totals row */}
              {leaderboard.length > 0 && (
                <div className="ta-totals-row">
                  <span className="ta-col-rank" />
                  <span className="ta-col-name ta-totals-label">TOTAL</span>
                  <span className="ta-col-num"><span className="ta-val sage">{fmtMin(totals.job)}</span></span>
                  <span className="ta-col-num"><span className="ta-val purple">{fmtMin(totals.drive)}</span></span>
                  <span className="ta-col-num"><span className="ta-val gold">{fmtMin(totals.down)}</span></span>
                  <span className="ta-col-num"><span className="ta-val red">-{formatCurrency(totals.downCost).replace('.00', '')}</span></span>
                </div>
              )}
            </div>

            {/* How it's calculated */}
            <div className="ta-legend-grid">
              <div className="ta-legend-card">
                <h4>How It's Calculated</h4>
                <div className="ta-legend-item"><span className="ta-legend-dot" style={{ background: 'var(--sage)' }} /><strong>Job Time</strong> — Actual duration (Start → Complete) split among workers on that job</div>
                <div className="ta-legend-item"><span className="ta-legend-dot" style={{ background: '#6366f1' }} /><strong>Drive Time</strong> — Each person's chronological job-to-job gaps (includes cross-route helper drives)</div>
                <div className="ta-legend-item"><span className="ta-legend-dot" style={{ background: '#f59e0b' }} /><strong>Downtime</strong> — Clocked hours minus job time minus drive time</div>
                <div className="ta-legend-note">Cost = downtime hours × employee hourly rate. Helper drives between routes are counted as drive time, not downtime.</div>
              </div>
              <div className="ta-legend-card">
                <h4>What Causes Downtime</h4>
                <div className="ta-legend-item"><Car size={13} /> Gaps between jobs (unoptimized routes)</div>
                <div className="ta-legend-item"><Clock size={13} /> Late starts or early finishes</div>
                <div className="ta-legend-item"><Timer size={13} /> Extended breaks or lunch</div>
                <div className="ta-callout">
                  <AlertTriangle size={13} /> Click any employee to see their daily breakdown and identify patterns.
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {activeReport === 'communications' && (() => {
        const commLogs = (state.communicationLogs || [])
          .filter(log => {
            const logDate = parseISO(log.communicationDate || log.createdAt);
            return isWithinInterval(logDate, { start: startDate, end: endDate });
          })
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        // Stats
        const byType = { call: 0, text: 0, email: 0, in_person: 0 };
        const byPerson = {};
        const byDay = {};
        commLogs.forEach(log => {
          byType[log.communicationType] = (byType[log.communicationType] || 0) + 1;
          const person = log.recordedByName || 'Unknown';
          byPerson[person] = (byPerson[person] || 0) + 1;
          const day = log.communicationDate || format(parseISO(log.createdAt), 'yyyy-MM-dd');
          if (!byDay[day]) byDay[day] = [];
          byDay[day].push(log);
        });
        const dayEntries = Object.entries(byDay).sort(([a], [b]) => b.localeCompare(a));

        const typeLabels = { call: 'Calls', text: 'Texts', email: 'Emails', in_person: 'In Person' };
        const typeIcons = { call: Phone, text: MessageSquare, email: Mail, in_person: Users };
        const typeColors = { call: '#2563eb', text: '#059669', email: '#d97706', in_person: '#7c3aed' };

        return (
          <div className="report-section">
            <h3><Phone size={20} /> Communication Log Report</h3>

            {/* Summary cards */}
            <div className="summary-cards">
              <div className="summary-card">
                <div className="card-value">{commLogs.length}</div>
                <div className="card-label">Total Communications</div>
              </div>
              {Object.entries(byType).filter(([, v]) => v > 0).map(([type, count]) => {
                const Icon = typeIcons[type];
                return (
                  <div key={type} className="summary-card">
                    <div className="card-value" style={{ color: typeColors[type] }}>
                      {count}
                    </div>
                    <div className="card-label" style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
                      <Icon size={14} /> {typeLabels[type]}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* By person table */}
            {Object.keys(byPerson).length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>By Team Member</h4>
                <div className="data-table">
                  <div className="table-header">
                    <span>Name</span>
                    <span style={{ textAlign: 'right' }}>Count</span>
                  </div>
                  {Object.entries(byPerson).sort(([,a],[,b]) => b - a).map(([name, count]) => (
                    <div key={name} className="table-row">
                      <span>{name}</span>
                      <span style={{ textAlign: 'right', fontWeight: 600 }}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Day-by-day log */}
            <h4 style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Daily Log</h4>
            {dayEntries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9ca3af' }}>
                <Phone size={32} style={{ marginBottom: 8 }} />
                <p>No communications logged in this date range</p>
              </div>
            ) : (
              dayEntries.map(([day, logs]) => (
                <div key={day} style={{ marginBottom: 16 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', background: '#f9fafb', borderRadius: 8,
                    fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 4
                  }}>
                    <span>{format(parseISO(day), 'EEEE, MMM d, yyyy')}</span>
                    <span style={{
                      fontSize: 11, color: '#fff', background: '#2c5f4e',
                      padding: '2px 8px', borderRadius: 10
                    }}>{logs.length}</span>
                  </div>
                  <div className="data-table" style={{ marginBottom: 0 }}>
                    {logs.map(log => {
                      const Icon = typeIcons[log.communicationType] || Phone;
                      return (
                        <div key={log.id} className="table-row" style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 12px' }}>
                          <div style={{
                            width: 28, height: 28, borderRadius: 6,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: log.communicationType === 'call' ? '#dbeafe' :
                                       log.communicationType === 'text' ? '#d1fae5' :
                                       log.communicationType === 'email' ? '#fef3c7' : '#ede9fe',
                            color: typeColors[log.communicationType],
                            flexShrink: 0
                          }}>
                            <Icon size={14} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontWeight: 600, fontSize: 14 }}>{log.clientName || 'OTHER'}</span>
                              <span style={{ fontSize: 12, color: '#9ca3af' }}>{log.communicationTime || ''}</span>
                            </div>
                            <p style={{ fontSize: 13, color: '#4b5563', margin: '4px 0', lineHeight: 1.4 }}>{log.note}</p>
                            <div style={{ fontSize: 11, color: '#9ca3af', display: 'flex', gap: 12 }}>
                              <span>{typeLabels[log.communicationType] || log.communicationType}</span>
                              <span>Logged by {log.recordedByName}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        );
      })()}

      {/* ===== TRENDS TAB ===== */}
      {activeReport === 'trends' && (() => {
        const { periods, current, changes } = trendData;
        const periodLabel = trendPeriod === 'weekly' ? 'Week' : 'Month';

        // Helper: render SVG sparkline area chart
        const TrendChart = ({ data, dataKey, color, prefix = '', suffix = '', formatFn }) => {
          if (!data || data.length === 0) return null;
          const values = data.map(d => d[dataKey]);
          const max = Math.max(...values, 1);
          const min = Math.min(...values, 0);
          const range = max - min || 1;
          const w = 100;
          const h = 40;
          const padding = 2;
          const chartW = w - padding * 2;
          const chartH = h - padding * 2;

          const points = values.map((v, i) => {
            const x = padding + (i / (values.length - 1 || 1)) * chartW;
            const y = padding + chartH - ((v - min) / range) * chartH;
            return { x, y, v };
          });

          const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
          const areaPath = `${linePath} L${points[points.length - 1].x.toFixed(1)},${h - padding} L${padding},${h - padding} Z`;

          const displayVal = (v) => {
            if (formatFn) return formatFn(v);
            if (prefix === '$') return formatCurrency(v);
            if (suffix === '%') return `${v.toFixed(1)}%`;
            if (suffix === ' min') return `${Math.round(v)} min`;
            if (suffix === ' hrs') return `${v.toFixed(1)} hrs`;
            return typeof v === 'number' ? (Number.isInteger(v) ? v : v.toFixed(1)) : v;
          };

          return (
            <svg viewBox={`0 0 ${w} ${h}`} className="trend-sparkline" preserveAspectRatio="none">
              <defs>
                <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity="0.3" />
                  <stop offset="100%" stopColor={color} stopOpacity="0.02" />
                </linearGradient>
              </defs>
              <path d={areaPath} fill={`url(#grad-${dataKey})`} />
              <path d={linePath} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              {points.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r="1.2" fill={color}>
                  <title>{data[i].shortLabel}: {displayVal(p.v)}</title>
                </circle>
              ))}
            </svg>
          );
        };

        // Helper: render full-width line chart with axes and multi-line support
        const FullChart = ({ data, lines, height = 220 }) => {
          if (!data || data.length === 0) return <div className="empty-report">No data available.</div>;

          const w = 600;
          const h = height;
          const pl = 60; // padding left
          const pr = 20;
          const pt = 20;
          const pb = 40;
          const chartW = w - pl - pr;
          const chartH = h - pt - pb;

          // Compute global min/max across all lines
          let allValues = [];
          lines.forEach(line => {
            data.forEach(d => allValues.push(d[line.key] || 0));
          });
          const rawMax = Math.max(...allValues, 1);
          const rawMin = Math.min(0, ...allValues);
          const range = rawMax - rawMin || 1;

          // Y-axis ticks
          const nTicks = 5;
          const step = range / (nTicks - 1);
          const yTicks = Array.from({ length: nTicks }, (_, i) => rawMin + step * i);

          const getY = (v) => pt + chartH - ((v - rawMin) / range) * chartH;
          const getX = (i) => pl + (i / (data.length - 1 || 1)) * chartW;

          const formatYLabel = (v) => {
            if (lines[0].prefix === '$') {
              if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
              return `$${Math.round(v)}`;
            }
            if (lines[0].suffix === '%') return `${v.toFixed(0)}%`;
            if (lines[0].suffix === ' hrs') return `${v.toFixed(0)}h`;
            if (lines[0].suffix === ' min') return `${Math.round(v)}m`;
            return Number.isInteger(v) ? v : v.toFixed(1);
          };

          return (
            <div className="trend-full-chart-wrap">
              <svg viewBox={`0 0 ${w} ${h}`} className="trend-full-chart" preserveAspectRatio="xMidYMid meet">
                {/* Grid lines */}
                {yTicks.map((tick, i) => (
                  <g key={i}>
                    <line x1={pl} y1={getY(tick)} x2={w - pr} y2={getY(tick)} stroke="#e5e7eb" strokeWidth="0.5" />
                    <text x={pl - 8} y={getY(tick) + 4} textAnchor="end" className="trend-axis-label">{formatYLabel(tick)}</text>
                  </g>
                ))}

                {/* X-axis labels */}
                {data.map((d, i) => {
                  // Show every label for <= 12 items, otherwise skip
                  const showLabel = data.length <= 13 || i % Math.ceil(data.length / 12) === 0;
                  return showLabel ? (
                    <text key={i} x={getX(i)} y={h - pb + 18} textAnchor="middle" className="trend-axis-label">{d.shortLabel}</text>
                  ) : null;
                })}

                {/* Lines */}
                {lines.map((line, li) => {
                  const points = data.map((d, i) => ({ x: getX(i), y: getY(d[line.key] || 0), v: d[line.key] || 0 }));
                  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
                  // Area fill
                  const areaPath = `${path} L${points[points.length - 1].x.toFixed(1)},${getY(rawMin)} L${points[0].x.toFixed(1)},${getY(rawMin)} Z`;
                  return (
                    <g key={li}>
                      {lines.length === 1 && (
                        <path d={areaPath} fill={line.color} fillOpacity="0.08" />
                      )}
                      <path d={path} fill="none" stroke={line.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      {points.map((p, i) => (
                        <circle key={i} cx={p.x} cy={p.y} r="3" fill="white" stroke={line.color} strokeWidth="1.5">
                          <title>{data[i].label}: {line.prefix === '$' ? formatCurrency(p.v) : (line.suffix === '%' ? p.v.toFixed(1) + '%' : (Number.isInteger(p.v) ? p.v : p.v.toFixed(1)) + (line.suffix || ''))}</title>
                        </circle>
                      ))}
                    </g>
                  );
                })}
              </svg>
              {/* Legend */}
              {lines.length > 1 && (
                <div className="trend-chart-legend">
                  {lines.map((line, i) => (
                    <div key={i} className="trend-legend-item">
                      <span className="trend-legend-dot" style={{ background: line.color }} />
                      <span>{line.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        };

        const ChangeIndicator = ({ change, prefix = '', suffix = '', invert = false }) => {
          const isUp = change.percent > 0;
          const isGood = invert ? !isUp : isUp;
          const Icon = isUp ? ArrowUp : ArrowDown;
          if (Math.abs(change.percent) < 0.1) return <span className="trend-change neutral">—</span>;
          return (
            <span className={`trend-change ${isGood ? 'positive' : 'negative'}`}>
              <Icon size={12} />
              {Math.abs(change.percent).toFixed(1)}%
            </span>
          );
        };

        return (
          <div className="report-content">
            {/* Period toggle */}
            <div className="trend-period-toggle">
              <button className={`trend-period-btn ${trendPeriod === 'weekly' ? 'active' : ''}`} onClick={() => setTrendPeriod('weekly')}>
                Weekly (12 weeks)
              </button>
              <button className={`trend-period-btn ${trendPeriod === 'monthly' ? 'active' : ''}`} onClick={() => setTrendPeriod('monthly')}>
                Monthly (12 months)
              </button>
            </div>

            {/* KPI Cards with sparklines */}
            <div className="trend-kpi-grid">
              <div className="trend-kpi-card">
                <div className="trend-kpi-header">
                  <span className="trend-kpi-label">Revenue</span>
                  <ChangeIndicator change={changes.revenue} prefix="$" />
                </div>
                <div className="trend-kpi-value">{formatCurrency(current.revenue || 0)}</div>
                <div className="trend-kpi-sub">This {periodLabel.toLowerCase()}</div>
                <TrendChart data={periods} dataKey="revenue" color="#2c5f4e" prefix="$" />
              </div>

              <div className="trend-kpi-card">
                <div className="trend-kpi-header">
                  <span className="trend-kpi-label">Payroll</span>
                  <ChangeIndicator change={changes.payrollCost} prefix="$" invert />
                </div>
                <div className="trend-kpi-value">{formatCurrency(current.payrollCost || 0)}</div>
                <div className="trend-kpi-sub">This {periodLabel.toLowerCase()}</div>
                <TrendChart data={periods} dataKey="payrollCost" color="#6366f1" prefix="$" />
              </div>

              <div className="trend-kpi-card">
                <div className="trend-kpi-header">
                  <span className="trend-kpi-label">Jobs</span>
                  <ChangeIndicator change={changes.totalJobs} />
                </div>
                <div className="trend-kpi-value">{current.totalJobs || 0}</div>
                <div className="trend-kpi-sub">This {periodLabel.toLowerCase()}</div>
                <TrendChart data={periods} dataKey="totalJobs" color="#059669" />
              </div>

              <div className="trend-kpi-card">
                <div className="trend-kpi-header">
                  <span className="trend-kpi-label">Profit</span>
                  <ChangeIndicator change={changes.profit} prefix="$" />
                </div>
                <div className="trend-kpi-value">{formatCurrency(current.profit || 0)}</div>
                <div className="trend-kpi-sub">This {periodLabel.toLowerCase()}</div>
                <TrendChart data={periods} dataKey="profit" color={current.profit >= 0 ? '#22c55e' : '#ef4444'} prefix="$" />
              </div>
            </div>

            {/* Revenue Trend */}
            <div className="report-section">
              <h3><DollarSign size={18} /> Revenue Trend</h3>
              <FullChart
                data={periods}
                lines={[{ key: 'revenue', label: 'Revenue', color: '#2c5f4e', prefix: '$' }]}
              />
            </div>

            {/* Revenue vs Payroll */}
            <div className="report-section">
              <h3><TrendingUp size={18} /> Revenue vs Payroll</h3>
              <FullChart
                data={periods}
                lines={[
                  { key: 'revenue', label: 'Revenue', color: '#2c5f4e', prefix: '$' },
                  { key: 'payrollCost', label: 'Payroll', color: '#6366f1', prefix: '$' },
                  { key: 'profit', label: 'Profit', color: '#22c55e', prefix: '$' }
                ]}
              />
            </div>

            {/* Jobs Trend */}
            <div className="report-section">
              <h3><Briefcase size={18} /> Jobs Trend</h3>
              <FullChart
                data={periods}
                lines={[
                  { key: 'completedJobs', label: 'Completed', color: '#059669' },
                  { key: 'totalJobs', label: 'Total', color: '#6b7280' }
                ]}
              />
            </div>

            {/* Labor % Trend */}
            <div className="report-section">
              <h3><Percent size={18} /> Labor % Trend</h3>
              <FullChart
                data={periods}
                lines={[{ key: 'laborPercent', label: 'Labor %', color: '#f59e0b', suffix: '%' }]}
              />
              <div className="trend-target-line-note">Target: keep under 40%</div>
            </div>

            {/* Avg Job Value & Duration */}
            <div className="report-section">
              <h3><BarChart3 size={18} /> Avg Job Value & Duration</h3>
              <div className="trend-dual-charts">
                <div className="trend-dual-chart">
                  <h4>Avg Job Value</h4>
                  <FullChart
                    data={periods}
                    lines={[{ key: 'avgJobValue', label: 'Avg Value', color: '#2c5f4e', prefix: '$' }]}
                    height={160}
                  />
                </div>
                <div className="trend-dual-chart">
                  <h4>Avg Duration</h4>
                  <FullChart
                    data={periods}
                    lines={[{ key: 'avgDuration', label: 'Avg Duration', color: '#6366f1', suffix: ' min' }]}
                    height={160}
                  />
                </div>
              </div>
            </div>

            {/* Hours & Tips */}
            <div className="report-section">
              <h3><Clock size={18} /> Hours & Tips</h3>
              <div className="trend-dual-charts">
                <div className="trend-dual-chart">
                  <h4>Total Hours Worked</h4>
                  <FullChart
                    data={periods}
                    lines={[{ key: 'totalHours', label: 'Hours', color: '#059669', suffix: ' hrs' }]}
                    height={160}
                  />
                </div>
                <div className="trend-dual-chart">
                  <h4>Tips Collected</h4>
                  <FullChart
                    data={periods}
                    lines={[{ key: 'tips', label: 'Tips', color: '#d97706', prefix: '$' }]}
                    height={160}
                  />
                </div>
              </div>
            </div>

            {/* Data Table */}
            <div className="report-section">
              <h3><FileText size={18} /> {trendPeriod === 'weekly' ? 'Weekly' : 'Monthly'} Breakdown</h3>
              <div className="trend-table-wrap">
                <table className="trend-table">
                  <thead>
                    <tr>
                      <th>{periodLabel}</th>
                      <th>Revenue</th>
                      <th>Payroll</th>
                      <th>Profit</th>
                      <th>Labor %</th>
                      <th>Jobs</th>
                      <th>Avg Value</th>
                      <th>Hours</th>
                      <th>Tips</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...periods].reverse().map((p, i) => (
                      <tr key={i} className={i === 0 ? 'trend-current-row' : ''}>
                        <td className="trend-period-cell">{p.label}</td>
                        <td>{formatCurrency(p.revenue)}</td>
                        <td>{formatCurrency(p.payrollCost)}</td>
                        <td className={p.profit >= 0 ? 'positive' : 'negative'}>{formatCurrency(p.profit)}</td>
                        <td className={p.laborPercent > 40 ? 'negative' : ''}>{p.laborPercent.toFixed(1)}%</td>
                        <td>{p.totalJobs}</td>
                        <td>{formatCurrency(p.avgJobValue)}</td>
                        <td>{p.totalHours.toFixed(1)}</td>
                        <td>{formatCurrency(p.tips)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ===== TIPS REPORT TAB ===== */}
      {activeReport === 'tips' && (() => {
        // Safely convert date values
        const toDate = (val) => {
          if (!val) return null;
          try {
            if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
            if (typeof val.toDate === 'function') return val.toDate(); // Firestore Timestamp
            if (typeof val === 'string') { const d = new Date(val); return isNaN(d.getTime()) ? null : d; }
            if (typeof val === 'number') return new Date(val);
          } catch { return null; }
          return null;
        };

        // Build employee name lookup
        const empNameMap = {};
        (state.employees || []).forEach(emp => {
          empNameMap[emp.id] = `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || 'Unknown';
        });
        const getEmpName = (id, fallback) => empNameMap[id] || fallback || 'Unknown';

        // Get invoices with tips in range
        // Use paidAt first, fall back to updatedAt/createdAt/invoiceDate for paid invoices
        const invoicesWithTips = (state.invoices || []).filter(inv => {
          try {
            const tip = Number(inv.tipAmount);
            if (!tip || tip <= 0) return false;

            // Find the best date to filter by
            const dateVal = toDate(inv.paidAt)
              || (inv.status === 'paid' ? (toDate(inv.updatedAt) || toDate(inv.createdAt) || toDate(inv.invoiceDate)) : null);
            if (!dateVal) return false;
            return isWithinInterval(dateVal, { start: startDate, end: endDate });
          } catch { return false; }
        });

        // Build tip entries with splits
        const tipEntries = [];
        invoicesWithTips.forEach(inv => {
          try {
            const tipTotal = Number(inv.tipAmount) || 0;
            const bestDate = inv.paidAt || inv.updatedAt || inv.createdAt || inv.invoiceDate;

            if (inv.tipSplit && Array.isArray(inv.tipSplit) && inv.tipSplit.length > 0) {
              inv.tipSplit.forEach(split => {
                const empId = split.employeeId || 'unknown';
                tipEntries.push({
                  invoiceId: inv.id,
                  invoiceNumber: inv.invoiceNumber || '',
                  clientName: inv.clientName || '',
                  paidDate: bestDate,
                  employeeId: empId,
                  employeeName: getEmpName(empId, split.employeeName),
                  tipAmount: Number(split.amount) || 0,
                  totalTip: tipTotal,
                  teamSize: inv.tipSplit.length
                });
              });
            } else {
              // Fallback: split among job team
              const job = inv.jobId ? (state.jobs || []).find(j => j.id === inv.jobId) : null;
              const teamMembers = job ? [...new Set([...(job.team || []), ...(job.helpers || [])])] : [];
              if (teamMembers.length > 0) {
                const splitAmount = Math.round((tipTotal / teamMembers.length) * 100) / 100;
                teamMembers.forEach(empId => {
                  tipEntries.push({
                    invoiceId: inv.id,
                    invoiceNumber: inv.invoiceNumber || '',
                    clientName: inv.clientName || '',
                    paidDate: bestDate,
                    employeeId: empId,
                    employeeName: getEmpName(empId),
                    tipAmount: splitAmount,
                    totalTip: tipTotal,
                    teamSize: teamMembers.length
                  });
                });
              } else {
                tipEntries.push({
                  invoiceId: inv.id,
                  invoiceNumber: inv.invoiceNumber || '',
                  clientName: inv.clientName || '',
                  paidDate: bestDate,
                  employeeId: 'unassigned',
                  employeeName: 'Unassigned',
                  tipAmount: tipTotal,
                  totalTip: tipTotal,
                  teamSize: 1
                });
              }
            }
          } catch (err) {
            console.error('Error processing tip entry:', err);
          }
        });

        // Aggregate totals per employee
        const empTotals = {};
        tipEntries.forEach(tip => {
          if (!empTotals[tip.employeeId]) {
            empTotals[tip.employeeId] = { name: tip.employeeName, total: 0, count: 0 };
          }
          empTotals[tip.employeeId].total += tip.tipAmount;
          empTotals[tip.employeeId].count += 1;
        });
        const empList = Object.entries(empTotals)
          .map(([id, d]) => ({ id, name: d.name, total: d.total, count: d.count, avg: d.count > 0 ? d.total / d.count : 0 }))
          .sort((a, b) => b.total - a.total);

        const grandTotal = tipEntries.reduce((s, t) => s + t.tipAmount, 0);
        const invoiceCount = new Set(tipEntries.map(t => t.invoiceId)).size;

        // Export CSV
        const safeFormat = (date, fmt) => { try { return format(date, fmt); } catch { return ''; } };
        const exportTipsCsv = () => {
          try {
            const lines = [];
            lines.push(['Tips Report']);
            lines.push([`${safeFormat(startDate, 'MM/dd/yyyy')} - ${safeFormat(endDate, 'MM/dd/yyyy')}`]);
            lines.push([]);
            lines.push(['Employee', 'Tips Earned', 'Tip Count', 'Avg per Tip']);
            empList.forEach(e => lines.push([e.name, e.total.toFixed(2), e.count, e.avg.toFixed(2)]));
            lines.push([]);
            lines.push(['TOTAL', grandTotal.toFixed(2)]);
            lines.push([]);
            lines.push(['Date Paid', 'Invoice', 'Client', 'Employee', 'Split Amount', 'Total Tip']);
            tipEntries.forEach(t => {
              const pd = toDate(t.paidDate);
              lines.push([pd ? safeFormat(pd, 'MM/dd/yyyy') : '', t.invoiceNumber, `"${t.clientName}"`, `"${t.employeeName}"`, t.tipAmount.toFixed(2), t.totalTip.toFixed(2)]);
            });
            const csv = lines.map(r => r.join(',')).join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `tips-report-${safeFormat(startDate, 'yyyy-MM-dd')}-to-${safeFormat(endDate, 'yyyy-MM-dd')}.csv`;
            a.click();
          } catch (err) { console.error('CSV export error:', err); }
        };

        return (
          <div className="report-content">
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
              <button className="btn-export" onClick={exportTipsCsv} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Download size={16} /> Export CSV
              </button>
            </div>

            {/* KPI Cards */}
            <div className="kpi-grid">
              <div className="kpi-card primary">
                <div className="kpi-icon"><DollarSign size={20} /></div>
                <div className="kpi-value">{formatCurrency(grandTotal)}</div>
                <div className="kpi-label">Total Tips</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-icon"><Users size={20} /></div>
                <div className="kpi-value">{empList.length}</div>
                <div className="kpi-label">Employees</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-icon"><FileText size={20} /></div>
                <div className="kpi-value">{invoiceCount}</div>
                <div className="kpi-label">Invoices with Tips</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-icon"><TrendingUp size={20} /></div>
                <div className="kpi-value">{formatCurrency(tipEntries.length > 0 ? grandTotal / tipEntries.length : 0)}</div>
                <div className="kpi-label">Avg per Entry</div>
              </div>
            </div>

            {/* Totals per Employee */}
            {empList.length > 0 && (
              <div className="report-table-section" style={{ marginTop: '24px' }}>
                <h3 style={{ marginBottom: '12px' }}>Tips by Employee</h3>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th style={{ textAlign: 'right' }}>Tips Earned</th>
                      <th style={{ textAlign: 'right' }}>Tip Count</th>
                      <th style={{ textAlign: 'right' }}>Avg per Tip</th>
                    </tr>
                  </thead>
                  <tbody>
                    {empList.map(emp => (
                      <tr key={emp.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                              width: '28px', height: '28px', borderRadius: '50%',
                              background: 'var(--sage-pale, #e8f0e8)', color: 'var(--sage, #5a7d5a)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '12px', fontWeight: 600
                            }}>{emp.name.charAt(0)}</span>
                            {emp.name}
                          </div>
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(emp.total)}</td>
                        <td style={{ textAlign: 'right' }}>{emp.count}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(emp.avg)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ fontWeight: 700 }}>
                      <td>Total</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(grandTotal)}</td>
                      <td style={{ textAlign: 'right' }}>{tipEntries.length}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* Detail Table */}
            <div className="report-table-section" style={{ marginTop: '24px' }}>
              <h3 style={{ marginBottom: '12px' }}>Tip Details</h3>
              {tipEntries.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px 16px', color: 'var(--text-muted, #888)' }}>
                  <DollarSign size={48} style={{ opacity: 0.3 }} />
                  <p style={{ marginTop: '12px' }}>No tips found for the selected date range</p>
                </div>
              ) : (
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Date Paid</th>
                      <th>Invoice</th>
                      <th>Client</th>
                      <th>Employee</th>
                      <th style={{ textAlign: 'right' }}>Split</th>
                      <th style={{ textAlign: 'right' }}>Total Tip</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...tipEntries]
                      .sort((a, b) => {
                        const dateA = toDate(a.paidDate);
                        const dateB = toDate(b.paidDate);
                        return (dateB?.getTime() || 0) - (dateA?.getTime() || 0);
                      })
                      .map((tip, idx) => {
                        const pd = toDate(tip.paidDate);
                        return (
                          <tr key={`${tip.invoiceId}-${tip.employeeId}-${idx}`}>
                            <td>{pd ? safeFormat(pd, 'MMM d, yyyy') : '—'}</td>
                            <td>{tip.invoiceNumber}</td>
                            <td>{tip.clientName}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{
                                  width: '22px', height: '22px', borderRadius: '50%',
                                  background: 'var(--sage-pale, #e8f0e8)', color: 'var(--sage, #5a7d5a)',
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '10px', fontWeight: 600
                                }}>{tip.employeeName.charAt(0)}</span>
                                {tip.employeeName}
                              </div>
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 500 }}>{formatCurrency(tip.tipAmount)}</td>
                            <td style={{ textAlign: 'right', color: 'var(--text-muted, #888)' }}>
                              {formatCurrency(tip.totalTip)}
                              {tip.teamSize > 1 && <span style={{ fontSize: '11px' }}> &divide; {tip.teamSize}</span>}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                  <tfoot>
                    <tr style={{ fontWeight: 700 }}>
                      <td colSpan={4}>Total</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(grandTotal)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        );
      })()}

      {/* Photo Viewer Modal */}
      {photoViewer && (
        <div className="photo-viewer-overlay" onClick={() => setPhotoViewer(null)}>
          <div className="photo-viewer-modal" onClick={e => e.stopPropagation()}>
            <button className="photo-viewer-close" onClick={() => setPhotoViewer(null)}><X size={24} /></button>
            <img src={photoViewer.url} alt={photoViewer.type || 'Photo'} />
            <div className="photo-viewer-info">
              <span className="photo-viewer-type">{photoViewer.type?.replace('_', ' ') || 'Photo'}</span>
              {photoViewer.uploadedAt && <span className="photo-viewer-time">{format(parseISO(photoViewer.uploadedAt), 'MMM d, h:mm a')}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Reports;